import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Plus, ShoppingBag, Save, Info, ArrowLeft, Zap, Hand, RotateCcw, Camera, Package, Scale, Heart, ZoomIn, Pencil, X, Loader2, Bookmark, FileText, LayoutGrid, History, ImagePlus, Mail, Link2, Share2 } from 'lucide-react';
import { tierLabels } from '../lib/fabricLabels';
import { resolveImageUrl } from '../lib/imageCache';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { formatPrice } from '../lib/utils';
import { sanitizeHtml } from '../lib/sanitize';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { Breadcrumbs } from '../components/website/Breadcrumbs';
import ARViewer from '../components/ARViewer';
import RecentlyViewed from '../components/RecentlyViewed';
import { ProductCmsContent } from '../components/website/ProductCmsContent';
import { ProductNextSteps } from '../components/website/ProductNextSteps';
import { OpennessCompareDialog } from '../components/configurator/OpennessCompareDialog';

// Extracted configurator components
import { SaveConfigDialog, SampleRequestModal, ShadeVisualizer, ColorZoomModal, MaterialCompareModal, QuoteBuilder } from '../components/configurator';
import { resetPreviewPrefs } from '../components/configurator/usePreviewPrefs';
import { SharePreview } from '../components/configurator/SharePreview';
import { FabricGalleryDialog } from '../components/configurator/fabric-gallery/FabricGalleryDialog';
import { flattenFabrics } from '../components/configurator/fabric-gallery/flattenFabrics';
import { RoomRenderDialog } from '../components/configurator/fabric-gallery/RoomRenderDialog';
import { EmailDesignDialog } from '../components/configurator/EmailDesignDialog';
import { MobilePreviewPanel } from '../components/configurator/MobilePreviewPanel';
import { splitRoomLabel } from '../lib/cartGroups';
import { StepFooter } from '../components/configurator/StepFooter';
import { FoldStyleStep } from '../components/configurator/FoldStyleStep';
import { isRomanFabric } from '../components/configurator/ConfiguratorShadePreview';
import { CountUpPrice } from '../components/configurator/CountUpPrice';
import { RoomPromptDialog } from '../components/configurator/RoomPromptDialog';
import { ROMAN_FOLDS, setRomanFold, onRomanFoldChange, setRomanLining, onRomanLiningChange, FOLD_TO_PRICE_ID, priceIdToFold, foldLabel, liningLabel } from '../lib/romanFold';
import { CompareFoldsDialog } from '../components/configurator/CompareFoldsDialog';
import { CollectionHoverPreview } from '../components/configurator/CollectionHoverPreview';
import { ConfiguratorProgress } from '../components/configurator/ConfiguratorProgress';
import { brandLabel } from '../lib/brandNames';
import { RoomLabelPicker } from '../components/configurator/RoomLabelPicker';
import AddAnotherShadePanel from '../components/configurator/AddAnotherShadePanel';
import { ShareDesignDialog, canNativeShare, nativeShare } from '../components/configurator/ShareDesignDialog';
import { CompareDesignsDialog } from '../components/configurator/CompareDesignsDialog';
import { readCompareDesigns, writeCompareDesigns, MAX_COMPARE } from '../hooks/useDesignCompare';
import { fetchVisibleCollections } from '../lib/fabricCatalog';
import { preloadCollectionScenes, sceneThumb } from '../lib/sceneImages';
import { readConfigDraft, writeConfigDraft, clearConfigDraft, draftHasSelections } from '../hooks/useConfigDraft';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const initialConfig = (productType) => ({
  product_type: productType,
  fabric_material: '',
  fabric_color: '',
  mount_type: '',
  width: '',
  height: '',
  control_type: '',
  control_position: '',
  motor_type: '', // battery, hardwired-dc, hardwired-ac
  motor_communication: '', // two-way, one-way
  bracket_type: '', // round, square
  bracket_color: '',
  chain_color: 'white',
  roll_direction: '', // standard, reverse
  roll_type: 'regular',
  bottom_bar_style: 'sewn-in',
  pleat_style: 'ripplefold',
  track_type: 'ceiling-track',
  fold_style: 'flat-fold',
  lining: 'unlined',
  optional_features: [],
  room_label: '',
  window_label: '',
  selected_openness: '',
  quantity: 1,
  needs_installation: false,
});

// Pre-rendered visualization images for each roller shade color (consistent room background)
const COLOR_VISUALIZATION_IMAGES = {
  'snow': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/99acf286c2742f5e7a91a1b2c236a14f6e2e4ce79467f2b8990d93d63bdd8eef.png',
  'ivory': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/ffd60bfbf2b0b56d14e791c91c41e71e2c63b9516c1e9b46991bde57c1aaba32.png',
  'linen': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/3e8e0304ccf05e3ea36322f3c848598b690de7b8f8d8bbc141b1e59c2798f643.png',
  'dove': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/36e30a30410db86c11024f28e223d3baccd9ea1e9945fc551b4aa9a4fe85a130.png',
  'charcoal': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/7f55415f67d4536f6f9b0c6d8b4ee831080d6c83b7d5f79d565aebe62d499fb1.png',
  'espresso': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/7664704501d6d5e2eef2aa8700db92f629f469c3a6d3016e4bbf55467690b1f0.png',
  'sage': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/37bae71bd143ef9942a23c8ea1389ba767cd31b32825de0ee0149a077ec00db2.png',
  'slate': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/a17d10aee87e3ecf0f6a9011d4c0e9ea97b3d27d69e05ecec680e794a584dff2.png',
  'sand': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/9cef4d92d6b430620f6fcfa7ed577c673ab142345c0ea2c2717e1859d3e5e4d5.png',
  'pearl': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/50f222fda246290cdff0180fd3a0b678365915794e37cccac452f74e256e722a.png',
  'navy': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/dbeefce7f906924fb67e441fda4515a41e0b674e9703ad3d2d3518687fe02f38.png',
  'blush': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/cd8b72973bafa32d2b07038a468fcfdb73e042ee51a7a2ff8cc86ddf20229d0a.png',
  'terracotta': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/ff45bf489d15500e63ac83c91f54c9584e74ad5b684f28e01092f0d1c3d61540.png',
  'forest': 'https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/079a53592b6b5e35f19c1dbb8236d9d4ab4200cb5b907bff1ef8cdc0d701f3bd.png',
};

// Fallback product info if CMS data not available
const PRODUCT_INFO = {
  'roller-shades': {
    name: 'Custom Roller Shades',
    description: 'Clean, modern roller shades custom-made to fit your space. Available in motorized, continuous loop, and cordless options.',
    heroImage: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1200&q=80',
    previewBg: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
    steps: ['Fabric', 'Mount', 'Size', 'Control', 'Hardware', 'Extras', 'Review'],
    hasRollType: true,
    hasBottomBar: true,
    previewType: 'roller',
  },
  'motorized-drapes': {
    name: 'Motorized Drapes',
    description: 'Elegant motorized drapery systems for modern interiors with smooth operation and smart home integration.',
    heroImage: 'https://images.unsplash.com/photo-1754611331891-949e7838f199?w=1200&q=80',
    previewBg: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1200&q=80',
    steps: ['Fabric', 'Mount', 'Size', 'Control', 'Pleat & Track', 'Extras', 'Review'],
    hasPleatStyle: true,
    hasTrackType: true,
    previewType: 'drape',
  },
  'roman-shades': {
    name: 'Roman Shades',
    description: 'Custom Roman shades crafted for softness, style, and function with elegant folds and wide fabric options.',
    heroImage: 'https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/aifi2w87_image.png',
    previewBg: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1200&q=80',
    steps: ['Fabric', 'Mount', 'Size', 'Control', 'Fold Style', 'Extras', 'Review'],
    hasFoldStyle: true,
    previewType: 'roman',
  },
};

export const ProductDetailPage = () => {
  const { productType: paramType } = useParams();
  // Get product type from URL path if not in params (for static routes)
  const productType = paramType || window.location.pathname.split('/').pop();
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [openSections, setOpenSections] = useState([1]); // Start with fabric open
  const [productData, setProductData] = useState(null);
  const [cmsProduct, setCmsProduct] = useState(null); // CMS product data
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(() => initialConfig(productType));
  const [hasDraft, setHasDraft] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [roomRenderOpen, setRoomRenderOpen] = useState(false);
  const [roomRenderResult, setRoomRenderResult] = useState(null);
  const [emailDesignOpen, setEmailDesignOpen] = useState(false);
  const [shareDesignOpen, setShareDesignOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareDesigns, setCompareDesigns] = useState(() => readCompareDesigns(productType));
  const sharedLoadedRef = useRef(null);
  const [price, setPrice] = useState({ unit_price: 0, total_price: 0, installation_price: 0 });
  const [installationPricing, setInstallationPricing] = useState(null);
  const [showARViewer, setShowARViewer] = useState(false);
  const [visualizations, setVisualizations] = useState([]);
  const [currentVisualization, setCurrentVisualization] = useState(null);
  const [isVisualizerFullscreen, setIsVisualizerFullscreen] = useState(false);
  const [fabricCollections, setFabricCollections] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedFabricMaterial, setSelectedFabricMaterial] = useState(null);  // For 4-tier hierarchy (Brand→Collection→Material→Color)
  // Legacy pricing material: 3-tier collections (e.g. DW) have no material layer, so fall back to the product's base material
  const fabricMaterialId = config.fabric_material || (fabricCollections.length > 0 ? productData?.materials?.[0]?.id || '' : '');
  const sizeValid = Number(config.width) >= 12 && Number(config.width) <= 144 && Number(config.height) >= 12 && Number(config.height) <= 120;
  const isRollerProduct = productType === 'roller-shades';
  const isMotorPage = /-motor$/.test(productType || '');
  // Cordless shades have no cord or chain, so there is no left/right control side to choose
  const needsControlPosition = config.control_type !== 'cordless';
  const isConfigReady = Boolean(
    config.fabric_color && fabricMaterialId && config.mount_type && sizeValid &&
    config.control_type && (!needsControlPosition || config.control_position) &&
    (config.control_type !== 'motorized' || (config.motor_type && config.motor_communication)) &&
    (!isRollerProduct || (config.roll_direction && config.bracket_type && config.bracket_color))
  );
  const roomLabelValid = Boolean(config.room_label && config.room_label.trim());
  // Cart / quote are only allowed once every step is done AND a room is chosen
  const isOrderReady = isConfigReady && roomLabelValid;
  const fullRoomLabel = config.window_label?.trim() ? `${config.room_label.trim()} — ${config.window_label.trim()}` : (config.room_label || '').trim();
  
  // Sample Request state
  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [sampleRequest, setSampleRequest] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: ''
  });
  const [submittingSample, setSubmittingSample] = useState(false);
  
  // Material Comparison state
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [materialsToCompare, setMaterialsToCompare] = useState([]);
  
  // Color Favorites state
  const [favoriteColors, setFavoriteColors] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  
  // Colors selected for sample request
  const [selectedSampleColors, setSelectedSampleColors] = useState([]);
  
  // Color Zoom Modal state
  const [colorZoomOpen, setColorZoomOpen] = useState(false);
  const [fabricGalleryOpen, setFabricGalleryOpen] = useState(false);
  const [zoomedColor, setZoomedColor] = useState(null);
  
  // Admin color editing state
  const [editingColor, setEditingColor] = useState(false);
  const [editColorData, setEditColorData] = useState({});
  const [savingColor, setSavingColor] = useState(false);
  
  // Save config as preset state
  const [saveConfigDialogOpen, setSaveConfigDialogOpen] = useState(false);
  const [configName, setConfigName] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Quote Builder state
  const [quoteBuilderOpen, setQuoteBuilderOpen] = useState(false);
  const [hasSavedQuoteItems, setHasSavedQuoteItems] = useState(() => { try { return JSON.parse(localStorage.getItem('apollo_quote_items') || '[]').length > 0; } catch { return false; } });
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { addToCart, replaceCartItem, cart, loading: cartLoading, itemCount, setIsOpen: setCartOpen } = useCart();
  const [editingCartItem, setEditingCartItem] = useState(null);
  const editLoadedRef = useRef(null);
  const [duplicatingIndex, setDuplicatingIndex] = useState(null);
  const [addedPanelOpen, setAddedPanelOpen] = useState(false);
  const [sessionShades, setSessionShades] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(`apollo_session_shades_${productType}`) || '[]'); } catch { return []; }
  });
  useEffect(() => { try { sessionStorage.setItem(`apollo_session_shades_${productType}`, JSON.stringify(sessionShades)); } catch { /* storage full */ } }, [sessionShades, productType]);
  const { isAuthenticated, token, user } = useAuth();
  const { addToRecentlyViewed } = useWishlist();
  
  // Check if user is admin
  const isAdmin = user?.is_admin === true || user?.role === 'admin' || user?.role_id === 'role_super_admin';

  const productInfo = PRODUCT_INFO[productType] || PRODUCT_INFO['roller-shades'];

  // Track recently viewed product
  useEffect(() => {
    if (cmsProduct && productType && cmsProduct.product_type === productType) {
      addToRecentlyViewed({
        id: cmsProduct.id || productType,
        product_type: productType,
        display_name: cmsProduct.display_name || cmsProduct.name,
        title: cmsProduct.display_name || cmsProduct.name,
        short_description: cmsProduct.short_description,
        images: cmsProduct.images,
        price_from: cmsProduct.price_from || cmsProduct.base_price,
        category_slug: cmsProduct.category_slug
      });
    }
  }, [cmsProduct, productType, addToRecentlyViewed]);

  // Fetch CMS product data for hero section
  useEffect(() => {
    const fetchCmsProduct = async () => {
      try {
        const response = await axios.get(`${API_URL.replace('/api', '')}/api/cms/product/${productType}`);
        setCmsProduct(response.data);
      } catch (error) {
        console.error('CMS product not found, using fallback:', error);
      }
    };
    fetchCmsProduct();
  }, [productType]);

  // Fetch product configurator data
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const response = await axios.get(`${API_URL}/products/${productType}/all`);
        setProductData(response.data);
      } catch (error) {
        console.error('Failed to fetch product data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProductData();
  }, [productType]);

  // Fetch available visualizations for this product type
  useEffect(() => {
    const fetchVisualizations = async () => {
      try {
        const response = await axios.get(`${API_URL}/visualizations?product_type=${productType}`);
        setVisualizations(response.data || []);
      } catch (error) {
        console.error('Failed to fetch visualizations:', error);
      }
    };
    fetchVisualizations();
  }, [productType]);

  // Fetch fabric collections from backend (filtered by product type)
  // Select a fabric from the gallery: sets brand → collection → (material) → color in one go
  const applyFabricSelection = (entry, brands = fabricCollections) => {
    const brand = brands.find(b => b.id === entry.brand_id);
    const collection = brand?.collections?.find(c => c.id === entry.collection_id);
    if (!brand || !collection) return;
    const material = entry.material_id ? collection.materials?.find(m => m.id === entry.material_id) : null;
    setSelectedBrand(brand);
    setSelectedCollection(collection);
    setSelectedFabricMaterial(material || null);
    setConfig(prev => ({
      ...prev,
      fabric_color: entry.id,
      fabric_material: material?.id || null,
      fabric_collection: collection.id,
      fabric_brand: brand.id
    }));
  };

  // Restore an exact configuration snapshot (draft, shared link, compare slot)
  const applySnapshot = (snap, brands = fabricCollections) => {
    const cfg = snap?.config || {};
    setConfig({ ...initialConfig(productType), ...cfg, product_type: productType });
    const color = cfg.fabric_color && flattenFabrics(brands).find(e => e.id === cfg.fabric_color);
    if (color) {
      applyFabricSelection(color, brands);
      return;
    }
    const brand = brands.find(b => b.id === snap.brand_id) || null;
    const collection = brand?.collections?.find(c => c.id === snap.collection_id) || null;
    setSelectedBrand(brand);
    setSelectedCollection(collection);
    setSelectedFabricMaterial(collection?.materials?.find(m => m.id === snap.material_id) || null);
    if (cfg.fabric_color) setConfig(prev => ({ ...prev, fabric_color: '' }));
  };

  useEffect(() => {
    const fetchFabricCollections = async () => {
      try {
        // Filter by product type to only show applicable brands (e.g., DW only for roller/solar/motorized)
        const brands = await fetchVisibleCollections(productType);
        setFabricCollections(brands);
        
        // Shared design link: /products/<type>?design=<shareId> → exact configuration
        const sharedId = searchParams.get('design');
        if (sharedId && sharedLoadedRef.current !== sharedId) {
          sharedLoadedRef.current = sharedId;
          try {
            const { data: shared } = await axios.get(`${API_URL}/shared-designs/${sharedId}`);
            applySnapshot(shared, brands);
            setShowConfigurator(true);
            setOpenSections([]);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            toast.success('Shared design loaded');
            clearConfigDraft(productType);
            setDraftReady(true);
            return;
          } catch (err) {
            toast.error(err.response?.data?.detail || 'This design link is no longer available');
          }
        }

        // Deep-link from the public fabric gallery: /products/<type>?fabric=<colorId>
        const requestedFabric = searchParams.get('fabric');
        const preselected = requestedFabric && flattenFabrics(brands).find(e => e.id === requestedFabric);
        if (preselected) {
          applyFabricSelection(preselected, brands);
          setShowConfigurator(true);
          setOpenSections(prev => (prev.includes(1) ? prev : [1, ...prev]));
          window.scrollTo({ top: 0, behavior: 'smooth' });
          toast.success(`${preselected.name} fabric selected`);
          clearConfigDraft(productType);
          setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('fabric'); return next; }, { replace: true });
        } else if (!searchParams.get('loadConfig') && !searchParams.get('editCartItem')) {
          // Resume a half-finished configuration saved in this browser
          const draft = readConfigDraft(productType);
          if (draft?.config) {
            applySnapshot(draft, brands);
            if (Array.isArray(draft.openSections) && draft.openSections.length) setOpenSections(draft.openSections);
            setHasDraft(true);
          } else {
            // Returning visitor: reopen on the Type / Collection (and color) they last chose
            try {
              const last = JSON.parse(localStorage.getItem(`apollo_last_fabric_${productType}`) || 'null');
              const brand = last && brands.find(b => b.id === last.brand_id);
              const collection = brand?.collections?.find(c => c.id === last.collection_id);
              if (brand && collection) {
                const entry = last.color_id && flattenFabrics(brands).find(e => e.id === last.color_id);
                if (entry) applyFabricSelection(entry, brands);
                else {
                  setSelectedBrand(brand); setSelectedCollection(collection);
                  const material = collection.materials?.find(m => m.id === last.material_id) || null;
                  setSelectedFabricMaterial(material);
                  setConfig(prev => ({ ...prev, fabric_brand: brand.id, fabric_collection: collection.id, fabric_material: material?.id || '' }));
                }
                setOpenSections(prev => (prev.includes(1) ? prev : [1, ...prev]));
              }
            } catch { /* ignore */ }
          }
        }
        setDraftReady(true);
      } catch (error) {
        console.error('Failed to fetch fabric collections:', error);
      }
    };
    fetchFabricCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  // Remember the last Type / Collection / color per product so a returning visitor lands where they left off
  useEffect(() => {
    if (!selectedBrand || !selectedCollection) return;
    try {
      localStorage.setItem(`apollo_last_fabric_${productType}`, JSON.stringify({ brand_id: selectedBrand.id, collection_id: selectedCollection.id, material_id: selectedFabricMaterial?.id || null, color_id: config.fabric_color || null }));
    } catch { /* storage full or unavailable — remembering the last fabric is optional */ }
  }, [productType, selectedBrand, selectedCollection, selectedFabricMaterial, config.fabric_color]);

  // Note: Visualization matching is done after selectedColor/selectedCollection are computed
  // See useEffect below the computed values section

  // Fetch installation pricing
  useEffect(() => {
    const fetchInstallationPricing = async () => {
      try {
        const response = await axios.get(`${API_URL}/installation-pricing`);
        setInstallationPricing(response.data);
      } catch (error) {
        console.error('Failed to fetch installation pricing:', error);
      }
    };
    fetchInstallationPricing();
  }, []);

  // Fetch user's favorite colors
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isAuthenticated || !token) return;
      try {
        const response = await axios.get(`${API_URL}/user/favorite-colors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteColors(response.data?.colors || []);
      } catch (error) {
        console.error('Failed to fetch favorites:', error);
      }
    };
    fetchFavorites();
  }, [isAuthenticated, token]);

  // Toggle color favorite
  const toggleColorFavorite = async (color) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }
    
    setLoadingFavorites(true);
    const colorData = {
      color_id: color.id,
      color_name: color.name,
      color_code: color.code,
      hex_color: color.hex_color,
      image_url: color.image_url,
      material_name: selectedFabricMaterial?.name,
      collection_name: selectedCollection?.name,
      brand_name: selectedBrand?.name
    };
    
    try {
      const isFavorite = favoriteColors.some(f => f.color_id === color.id);
      if (isFavorite) {
        await axios.delete(`${API_URL}/user/favorite-colors/${color.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteColors(prev => prev.filter(f => f.color_id !== color.id));
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API_URL}/user/favorite-colors`, colorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteColors(prev => [...prev, colorData]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    } finally {
      setLoadingFavorites(false);
    }
  };

  const isColorFavorite = (colorId) => {
    return favoriteColors.some(f => f.color_id === colorId);
  };

  // Toggle color for sample request
  const toggleSampleColor = (color) => {
    setSelectedSampleColors(prev => {
      const exists = prev.some(c => c.id === color.id);
      if (exists) {
        return prev.filter(c => c.id !== color.id);
      } else {
        return [...prev, {
          id: color.id,
          name: color.name,
          code: color.code,
          material_name: selectedFabricMaterial?.name,
          collection_name: selectedCollection?.name,
          brand_name: selectedBrand?.name
        }];
      }
    });
  };

  const isColorSelectedForSample = (colorId) => {
    return selectedSampleColors.some(c => c.id === colorId);
  };

  // Open color zoom modal
  const openColorZoom = (color) => {
    setZoomedColor(color);
    setEditingColor(false);
    setEditColorData({});
    setColorZoomOpen(true);
  };

  // Admin: Start editing color
  const startEditingColor = () => {
    if (!zoomedColor || !isAdmin) return;
    setEditColorData({
      name: zoomedColor.name || '',
      code: zoomedColor.code || '',
      hex_color: zoomedColor.hex_color || '',
      image_url: zoomedColor.image_url || ''
    });
    setEditingColor(true);
  };

  // Admin: Save color changes
  const saveColorChanges = async () => {
    if (!zoomedColor || !isAdmin || !token) return;
    
    setSavingColor(true);
    try {
      // Determine the parent (material or collection)
      const materialId = selectedFabricMaterial?.id;
      
      const response = await axios.put(
        `${API_URL}/admin/fabric-collections/colors/${zoomedColor.id}`,
        {
          name: editColorData.name,
          code: editColorData.code,
          hex_color: editColorData.hex_color,
          image_url: editColorData.image_url,
          material_id: materialId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Update local state
        setZoomedColor(prev => ({
          ...prev,
          name: editColorData.name,
          code: editColorData.code,
          hex_color: editColorData.hex_color,
          image_url: editColorData.image_url
        }));
        
        // Refresh fabric collections to show updated color
        try {
          setFabricCollections(await fetchVisibleCollections(productType, { force: true }));
        } catch (e) {
          console.warn('Refresh failed after color update', e);
        }
        
        toast.success('Color updated successfully');
        setEditingColor(false);
      }
    } catch (error) {
      console.error('Failed to update color:', error);
      toast.error(error.response?.data?.detail || 'Failed to update color');
    } finally {
      setSavingColor(false);
    }
  };

  useEffect(() => {
    const calculatePrice = async () => {
      // Price as soon as fabric + size are known; missing steps fall back to defaults and the result is flagged as an estimate
      if (!(fabricMaterialId && config.fabric_color && sizeValid)) {
        setPrice({ unit_price: 0, total_price: 0, installation_price: 0 });
        return;
      }
      const isEstimate = !isConfigReady;
      try {
          // Grab catalog material pricing (if set) to pass as override
          const matBasePrice = selectedFabricMaterial?.base_price || null;
          const matPriceSqft = selectedFabricMaterial?.price_per_sqft || null;

          const response = await axios.post(`${API_URL}/calculate-price`, {
            ...config,
            fabric_material: fabricMaterialId,
            width: Number(config.width),
            height: Number(config.height),
            mount_type: config.mount_type || productData?.mount_types?.[0]?.id || 'inside',
            control_type: config.control_type || productData?.control_types?.[0]?.id || 'chain',
            control_position: config.control_position || 'right',
            bracket_color: config.bracket_color || 'white',
            fabric_base_price: matBasePrice,
            fabric_price_per_sqft: matPriceSqft
          });
          
          setPrice({ ...response.data, is_estimate: isEstimate });
        } catch (error) {
          console.error('Price calculation failed:', error);
          // Fallback: basic local estimate using catalog material base_price or a safe default
          const bpSqft = selectedFabricMaterial?.base_price || selectedFabricMaterial?.price_per_sqft || 4.50;
          const widthFt = Number(config.width) / 12;
          const heightFt = Number(config.height) / 12;
          const sqFt = widthFt * heightFt;
          const fabricCost = sqFt * bpSqft;
          const basePrice = 89; // Safe default base
          setPrice({
            unit_price: basePrice + fabricCost,
            total_price: basePrice + fabricCost,
            installation_price: 0,
            is_estimate: true
          });
        }
    };
    calculatePrice();
  }, [config, selectedFabricMaterial, fabricMaterialId, sizeValid, isConfigReady]);

  // NOTE: This is for the OLD flat structure only (productData.colors)
  // When fabric collections are used, colors come from selectedCollection.colors
  const availableColors = useMemo(() => {
    if (!productData || !config.fabric_material) return [];
    // Only use productData colors if NO fabric collections are loaded
    return productData.colors.filter(color => color.material_ids.includes(config.fabric_material));
  }, [productData, config.fabric_material]);

  // Auto-select first color when material changes (OLD structure only - when no fabric collections)
  useEffect(() => {
    // Skip this effect if fabric collections are being used
    if (fabricCollections.length > 0) return;
    
    if (config.fabric_material && availableColors.length > 0) {
      const colorAvailable = availableColors.some(c => c.id === config.fabric_color);
      if (!colorAvailable && config.fabric_color) {
        setConfig(prev => ({ ...prev, fabric_color: '' }));
      }
    }
  }, [availableColors, config.fabric_material, config.fabric_color, fabricCollections.length]);

  // Edit an existing cart item: /products/<type>?editCartItem=<id> → load its config into the configurator
  useEffect(() => {
    const editId = searchParams.get('editCartItem');
    if (!editId || !fabricCollections.length || !cart.items?.length || editLoadedRef.current === editId) return;
    const item = cart.items.find(i => i.id === editId);
    if (!item) return;
    editLoadedRef.current = editId;
    const { room, window: win } = splitRoomLabel(item.config?.room_label || '');
    const cfg = { ...item.config, room_label: room, window_label: win, quantity: item.quantity || item.config?.quantity || 1 };
    applySnapshot({ config: cfg }, fabricCollections);
    setEditingCartItem({ id: item.id, label: item.config?.room_label || 'Shade' });
    setShowConfigurator(true);
    setOpenSections([]);
    clearConfigDraft(productType);
    setHasDraft(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.success(`Editing ${item.config?.room_label || 'shade'} — update it when you're done`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, fabricCollections, cart.items]);

  const cancelEditCartItem = () => {
    setEditingCartItem(null);
    editLoadedRef.current = null;
    searchParams.delete('editCartItem');
    setSearchParams(searchParams, { replace: true });
  };

  const handleUpdateCartItem = async () => {
    if (!isOrderReady || !editingCartItem) return;
    try {
      await replaceCartItem(editingCartItem.id, cartConfig());
      clearConfigDraft(productType);
      toast.success(`${fullRoomLabel} updated in your cart`);
      cancelEditCartItem();
      setCartOpen(true);
    } catch {
      toast.error('Failed to update the cart item');
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };
  // Motor pages only sell motorized operation
  useEffect(() => { if (isMotorPage && draftReady && !config.control_type) updateConfig('control_type', 'motorized'); }, [isMotorPage, draftReady, config.control_type]);
  // Phones: floating chat/WhatsApp step aside while the configurator (and its sticky footer) is open
  useEffect(() => {
    document.body.classList.toggle('fa-configurator', showConfigurator);
    return () => document.body.classList.remove('fa-configurator');
  }, [showConfigurator]);

  // Roman fold style: the Fold Style step and the Simulator / AI Scene fold picker share one choice
  useEffect(() => onRomanFoldChange(f => updateConfig('fold_style', FOLD_TO_PRICE_ID[f] || 'flat-fold')), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRomanFold(priceIdToFold(config.fold_style)); }, [config.fold_style]);
  useEffect(() => onRomanLiningChange(l => updateConfig('lining', l)), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRomanLining(config.lining || 'unlined'); }, [config.lining]);
  const [compareFoldsOpen, setCompareFoldsOpen] = useState(false);
  // Roman fabrics use the Lining choice instead of the generic blackout liner extra
  const romanFabricPicked = productType === 'roman-shades' || /\broman\b/i.test(selectedCollection?.name || '');
  useEffect(() => {
    if (romanFabricPicked && config.optional_features.includes('blackout-liner')) updateConfig('optional_features', config.optional_features.filter(id => id !== 'blackout-liner'));
  }, [romanFabricPicked, config.optional_features]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save progress: keep the in-browser draft in sync with every change
  useEffect(() => {
    if (!draftReady) return;
    if (!draftHasSelections(config) && !selectedBrand) {
      clearConfigDraft(productType);
      return;
    }
    writeConfigDraft(productType, {
      config,
      brand_id: selectedBrand?.id || null,
      collection_id: selectedCollection?.id || null,
      material_id: selectedFabricMaterial?.id || null,
      openSections,
    });
  }, [config, selectedBrand, selectedCollection, selectedFabricMaterial, openSections, draftReady, productType]);

  const startOver = () => {
    setConfig(initialConfig(productType));
    resetPreviewPrefs();
    setSelectedBrand(null);
    setSelectedCollection(null);
    setSelectedFabricMaterial(null);
    setOpenSections([1]);
    clearConfigDraft(productType);
    setHasDraft(false);
    // Clear session shades history
    setSessionShades([]);
    setAddedPanelOpen(false);
    toast.success('Configuration cleared');
  };

  // Load saved config from session storage (when clicking "Load" from saved configs page)
  useEffect(() => {
    if (searchParams.get('loadConfig') === 'true') {
      const savedConfig = sessionStorage.getItem('loadSavedConfig');
      if (savedConfig) {
        try {
          const parsed = JSON.parse(savedConfig);
          setConfig(prev => ({
            ...prev,
            ...parsed
          }));
          sessionStorage.removeItem('loadSavedConfig');
          toast.success('Configuration loaded!');
        } catch (e) {
          console.error('Failed to load saved config:', e);
        }
      }
    }
  }, [searchParams]);

  // Save current config as preset
  const handleSaveConfig = async () => {
    if (!configName.trim()) {
      toast.error('Please enter a name for your configuration');
      return;
    }
    
    if (!isAuthenticated) {
      toast.error('Please log in to save configurations');
      navigate('/login?redirect=' + window.location.pathname);
      return;
    }
    
    setSavingConfig(true);
    try {
      // Get the color name for the saved config
      const selectedColor = availableColors.find(c => c.id === config.fabric_color);
      
      await axios.post(
        `${API_URL}/user/saved-configs`,
        {
          name: configName.trim(),
          config: {
            ...cartConfig(),
            fabric_color_name: selectedColor?.name || config.fabric_color_name,
            fabric_color_hex: selectedColor?.hex_color || '',
            fabric_image_url: selectedColor?.image_url || ''
          },
          product_type: productType
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Configuration "${configName}" saved!`);
      setSaveConfigDialogOpen(false);
      setConfigName('');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleFeature = (featureId) => {
    setConfig(prev => ({
      ...prev,
      optional_features: prev.optional_features.includes(featureId)
        ? prev.optional_features.filter(f => f !== featureId)
        : [...prev.optional_features, featureId]
    }));
  };

  const toggleSection = (sectionId) => {
    setOpenSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Sample Request Functions
  const handleSampleRequestSubmit = async () => {
    if (!sampleRequest.customer_name || !sampleRequest.customer_email || !sampleRequest.shipping_address) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (selectedSampleColors.length === 0) {
      toast.error('Please select at least one color to sample');
      return;
    }
    
    try {
      setSubmittingSample(true);
      await axios.post(`${API_URL.replace('/api', '')}/api/fabric-collections/sample-request`, {
        ...sampleRequest,
        colors: selectedSampleColors
      });
      
      toast.success('Sample request submitted! Sample requests are typically processed and shipped within 2–3 business days, and often ship the next business day.');
      setSampleModalOpen(false);
      setSelectedSampleColors([]);
      setSampleRequest({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        shipping_address: '',
        city: '',
        state: '',
        zip_code: '',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to submit sample request');
      console.error(error);
    } finally {
      setSubmittingSample(false);
    }
  };

  // Material Comparison Functions
  const toggleMaterialComparison = (material, brand, collection) => {
    const materialWithContext = {
      ...material,
      brand_id: brand.id,
      brand_name: brand.name,
      collection_id: collection.id,
      collection_name: collection.name
    };
    
    setMaterialsToCompare(prev => {
      const exists = prev.find(m => m.id === material.id);
      if (exists) {
        return prev.filter(m => m.id !== material.id);
      }
      if (prev.length >= 3) {
        toast.error('You can compare up to 3 materials');
        return prev;
      }
      return [...prev, materialWithContext];
    });
  };

  const isMaterialInComparison = (materialId) => {
    return materialsToCompare.some(m => m.id === materialId);
  };

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1: return Boolean(config.fabric_color && (fabricCollections.length > 0 ? selectedCollection : config.fabric_material));
      case 2: return Boolean(config.mount_type);
      case 3: return sizeValid;
      case 4: return Boolean(config.control_type && (!needsControlPosition || config.control_position) &&
        (config.control_type !== 'motorized' || (config.motor_type && config.motor_communication)));
      case 5: return Boolean(config.roll_direction); // Roll Direction
      case 6: return Boolean(config.bracket_type && config.bracket_color); // Bracket Type
      case 7: return config.optional_features.length > 0; // Extras
      case 8: return Boolean(config.fold_style); // Fold Style
      default: return false;
    }
  };

  // Guided step flow: steps never auto-collapse; the user closes a step or moves on with the footer buttons
  const romanSelected = productType === 'roman-shades' || isRomanFabric(selectedCollection, null);
  const stepOrder = [1, ...(romanSelected ? [8] : []), 2, 3, 4, ...(isRollerProduct ? [5, 6] : []), 7];
  const stepNumber = (id) => String(stepOrder.indexOf(id) + 1);
  const stepTitles = { 1: 'Fabric', 2: 'Mount Type', 3: 'Size', 4: 'Control', 5: 'Roll Direction', 6: 'Bracket Type', 7: 'Extras', 8: 'Fold Style' };
  const stepTriggerId = (id) => (id === 7 ? 'step-extras-trigger' : `step-${id}-trigger`);
  const nextStepOf = (id) => stepOrder[stepOrder.indexOf(id) + 1] || null;
  const advanceFromStep = (id) => {
    const next = nextStepOf(id);
    setOpenSections(next ? [next] : []);
    setTimeout(() => {
      const target = next ? `[data-testid="${stepTriggerId(next)}"]` : '[data-testid="installation-option"]';
      const el = document.querySelector(target);
      if (el) {
        const rect = el.getBoundingClientRect();
        // Scroll so the next step header is near the top with some padding (accounting for sticky header)
        const offset = 100;
        if (rect.top < offset || rect.top > window.innerHeight * 0.6) {
          window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: 'smooth' });
        }
      }
    }, 180);
  };
  // Step 1's footer sits inside the open Collection box whenever a color grid is showing there
  const fabricColorsInline = Boolean(selectedCollection && (
    (!selectedCollection.materials?.length && selectedCollection.colors?.length > 0) ||
    (selectedFabricMaterial?.colors?.length > 0 && selectedCollection.materials?.some(m => m.id === selectedFabricMaterial.id))
  ));
  const jumpToStep = (id) => {
    setOpenSections(prev => (prev.includes(id) ? prev : [...prev, id]));
    setTimeout(() => {
      document.querySelector(`[data-testid="${stepTriggerId(id)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  };
  // Price reveal + share prompt after the design is complete
  const [celebrated, setCelebrated] = useState(0);

  // Site-wide configurator UI switches (admins can toggle fold scene thumbnails right in the Fold Style step)
  const [uiSettings, setUiSettings] = useState({ fold_thumbnails: true });
  useEffect(() => { axios.get(`${API_URL}/configurator-settings`).then(r => setUiSettings(r.data)).catch(() => {}); }, []);
  const toggleFoldThumbnails = async (on) => {
    setUiSettings(prev => ({ ...prev, fold_thumbnails: on }));
    try {
      await axios.put(`${API_URL}/admin/configurator-settings`, { fold_thumbnails: on }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(on ? 'Fold scene thumbnails on for all customers' : 'Fold cards now show the drawn icons for all customers');
    } catch {
      setUiSettings(prev => ({ ...prev, fold_thumbnails: !on }));
      toast.error('Could not update the setting');
    }
  };
  const sessionShadesRef = useRef(0);
  sessionShadesRef.current = sessionShades.length;
  // Guided progress toast whenever exactly one step becomes complete (skips draft restores / deep links)
  const stepDoneRef = useRef(null);
  const stepDone = stepOrder.map(id => isStepComplete(id) || (id === 7 && isConfigReady));
  const stepDoneKey = stepDone.join(',');
  useEffect(() => {
    const prev = stepDoneRef.current;
    stepDoneRef.current = { key: stepDoneKey, order: stepOrder.join(',') };
    if (!draftReady || !prev || prev.order !== stepOrder.join(',')) return;
    const before = prev.key.split(',');
    let newly = stepDone.map((d, i) => (d && before[i] !== 'true' ? stepOrder[i] : null)).filter(Boolean);
    if (newly.length === 2 && newly.includes(7)) newly = newly.filter(id => id !== 7); // Extras auto-completes with the last step
    if (newly.length !== 1) return;
    const left = stepDone.filter(d => !d).length;
    toast.success(left ? `${stepTitles[newly[0]]} ✓ — ${left} step${left === 1 ? '' : 's'} to your price` : `${stepTitles[newly[0]]} ✓ — your price is ready!`, { id: 'step-progress', duration: 2500 });
    if (!left) {
      setCelebrated(c => c + 1);
      const shadesAtFinish = sessionShadesRef.current;
      setTimeout(() => sessionShadesRef.current === shadesAtFinish && toast('Want to come back to this later?', {
        id: 'design-reminder', duration: 8000,
        description: 'Email yourself this design and price — reopen it anytime.',
        action: { label: 'Email it to me', onClick: () => setEmailDesignOpen(true) },
      }), 3200);
    }
    if (!left && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      const colors = ['#b45309', '#f59e0b', '#fde68a', '#171717', '#ffffff'];
      confetti({ particleCount: 90, spread: 75, startVelocity: 38, origin: { y: 0.7 }, colors, scalar: 0.9, disableForReducedMotion: true });
      setTimeout(() => confetti({ particleCount: 40, spread: 100, origin: { y: 0.65 }, colors, scalar: 0.8, disableForReducedMotion: true }), 220);
    }
  }, [stepDoneKey, draftReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Roman: the first time a color is picked, open Fold Style right under Fabric and suggest it
  const [foldSuggest, setFoldSuggest] = useState(false);
  const foldSuggestedRef = useRef(false);
  useEffect(() => {
    if (!romanSelected || !draftReady || !config.fabric_color || foldSuggestedRef.current) return;
    foldSuggestedRef.current = true;
    setFoldSuggest(true);
    setOpenSections(prev => (prev.includes(8) ? prev : [...prev, 8]));
    setTimeout(() => document.querySelector('[data-testid="step-8-trigger"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 350);
  }, [config.fabric_color, romanSelected, draftReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const foldAtSuggestRef = useRef(null);
  // ...then once a fold is chosen, suggest a lining the same way (hidden after a lining is picked)
  const [liningSuggest, setLiningSuggest] = useState(false);
  const liningAtSuggestRef = useRef(null);
  useEffect(() => {
    if (!foldSuggest) return;
    if (foldAtSuggestRef.current === null) { foldAtSuggestRef.current = config.fold_style; return; }
    if (config.fold_style === foldAtSuggestRef.current) return;
    setFoldSuggest(false);
    liningAtSuggestRef.current = config.lining || 'unlined';
    setLiningSuggest(true);
    setTimeout(() => document.getElementById('lining-picker')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  }, [config.fold_style, foldSuggest]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (liningSuggest && (config.lining || 'unlined') !== liningAtSuggestRef.current) setLiningSuggest(false); }, [config.lining, liningSuggest]);
  const renderStepFooter = (id) => (
    <StepFooter
      stepId={id}
      nextLabel={nextStepOf(id) ? stepTitles[nextStepOf(id)] : null}
      complete={isStepComplete(id)}
      onCollapse={() => setOpenSections(prev => prev.filter(s => s !== id))}
      onNext={() => advanceFromStep(id)}
    />
  );

  const getStepSummary = (stepId) => {
    switch (stepId) {
      case 1: 
        // Show full fabric selection path: Brand - Collection - Material - Color
        // Handle both 4-tier (Coulisse) and 3-tier (DW) hierarchies
        if (selectedBrand && selectedCollection && selectedColor) {
          if (selectedFabricMaterial) {
            // 4-tier: Brand - Collection - Material - Color
            return `${brandLabel(selectedBrand.name)} → ${selectedCollection.name} → ${selectedFabricMaterial.name} → ${selectedColor.name}`;
          } else {
            // 3-tier: Brand - Collection - Color (no material layer)
            return `${brandLabel(selectedBrand.name)} → ${selectedCollection.name} → ${selectedColor.name}`;
          }
        } else if (selectedBrand && selectedCollection) {
          // Partial selection - show what's selected so far
          if (selectedFabricMaterial) {
            return `${brandLabel(selectedBrand.name)} → ${selectedCollection.name} → ${selectedFabricMaterial.name}`;
          }
          return `${brandLabel(selectedBrand.name)} → ${selectedCollection.name}`;
        } else if (selectedMaterial && selectedColor) {
          return `${selectedMaterial.name} - ${selectedColor.name}`;
        }
        return 'Select fabric';
      case 2: 
        return productData?.mount_types?.find(m => m.id === config.mount_type)?.name || 'Select mount';
      case 3: 
        return config.width && config.height
          ? `${config.width}" W × ${config.height}" H (Qty: ${config.quantity})`
          : 'Enter width and height';
      case 4: 
        const control = productData?.control_types?.find(c => c.id === config.control_type);
        if (config.control_type === 'motorized') {
          const motorTypes = { 'battery': 'Battery', 'hardwired-dc': 'DC', 'hardwired-ac': 'AC' };
          return motorTypes[config.motor_type] ? `Motorized - ${motorTypes[config.motor_type]}` : 'Motorized - select motor type';
        }
        if (!control) return 'Select control';
        if (!needsControlPosition) return control.name;
        return config.control_position ? `${control.name} (${config.control_position})` : `${control.name} — choose control side`;
      case 5: // Roll Direction
        if (!config.roll_direction) return 'Select roll direction';
        return config.roll_direction === 'standard' ? 'Standard Roll' : 'Reverse Roll';
      case 6: // Bracket Type
        if (!config.bracket_type) return 'Select bracket type';
        return `${config.bracket_type === 'round' ? 'Round Bracket' : 'Square Bracket'}${config.bracket_color ? '' : ' — choose a color'}`;
      case 8:
        return `${foldLabel(priceIdToFold(config.fold_style))} fold · ${liningLabel(config.lining)}`;
      case 7: 
        return config.optional_features.length > 0 
          ? `${config.optional_features.length} feature(s) selected` 
          : 'No extras selected';
      default: return '';
    }
  };

  const cartConfig = () => ({
    ...config,
    room_label: fullRoomLabel,
    fabric_material: fabricMaterialId,
    width: Number(config.width),
    height: Number(config.height),
    fabric_base_price: selectedFabricMaterial?.base_price || null,
    fabric_price_per_sqft: selectedFabricMaterial?.price_per_sqft || null,
    fabric_brand_name: selectedBrand?.name || null,
    fabric_collection_name: [selectedCollection?.name, selectedFabricMaterial?.name].filter(Boolean).join(' · ') || null,
    fabric_color_name: selectedColor?.name || null,
  });

  const activeRoomRender = roomRenderResult?.color_id === config.fabric_color ? roomRenderResult : null;

  // Share link that reopens this exact configuration
  const createShareLink = async () => {
    const { data } = await axios.post(`${API_URL}/shared-designs`, {
      product_type: productType,
      config,
      brand_id: selectedBrand?.id || null,
      collection_id: selectedCollection?.id || null,
      material_id: selectedFabricMaterial?.id || null,
      label: selectedColor ? `${productInfo.name} · ${selectedColor.name}` : productInfo.name,
      preview: {
        origin: window.location.origin,
        title: selectedColor ? `${selectedColor.name} ${productInfo.name} — designed with Apollo` : `${productInfo.name} — designed with Apollo`,
        description: [fabricPathLabel(), sizeValid ? `${config.width}" × ${config.height}"` : null, romanSelected ? getStepSummary(8) : null,
          isConfigReady ? `${formatPrice(price.total_price || price.total || 0)}` : null].filter(Boolean).join(' · '),
        image_url: shareSceneUrl() || '',
      },
    });
    return `${window.location.origin}${data.share_path || data.path}`;
  };
  const fabricPathLabel = () => [selectedCollection?.name, selectedFabricMaterial?.name, selectedColor?.name].filter(Boolean).join(' · ');
  const shareSceneUrl = () => {
    if (!selectedColor) return null;
    if (romanSelected) {
      const f = priceIdToFold(config.fold_style) || 'flat';
      const rv = selectedColor.roman_fold_visualizations || {};
      const map = f === 'flat' ? (rv.flat || selectedColor.room_visualizations || {}) : (rv[f] || {});
      const u = map.penthouse_living || Object.values(map)[0];
      if (u) return u;
    }
    return selectedColor.room_visualization_url || selectedColor.ai_visualization_url || Object.values(selectedColor.room_visualizations || {})[0] || selectedColor.image_url || null;
  };

  // Compare designs (up to 3, browser-local)
  const fabricPath = [brandLabel(selectedBrand?.name), selectedCollection?.name, selectedFabricMaterial?.name].filter(Boolean).join(' → ');
  const persistCompare = (list) => { setCompareDesigns(list); writeCompareDesigns(productType, list); };
  const addCurrentToCompare = () => {
    if (!selectedColor) return toast.error('Select a fabric before saving a design to compare');
    if (compareDesigns.length >= MAX_COMPARE) return toast.error(`You can compare up to ${MAX_COMPARE} designs — remove one first`);
    const totalValue = isConfigReady ? (price.total_price || price.total || 0) : null;
    const snapshot = {
      id: `${Date.now()}`,
      saved_at: new Date().toISOString(),
      config,
      brand_id: selectedBrand?.id || null,
      collection_id: selectedCollection?.id || null,
      material_id: selectedFabricMaterial?.id || null,
      fabric: { name: selectedColor.name, image_url: selectedColor.image_url, hex: selectedColor.hex_color, path: fabricPath },
      rows: {
        Mount: config.mount_type ? getStepSummary(2) : '—',
        Size: sizeValid ? `${config.width}" × ${config.height}"${config.quantity > 1 ? ` × ${config.quantity}` : ''}` : '—',
        Control: config.control_type ? getStepSummary(4) : '—',
        ...(romanSelected ? { 'Fold style': getStepSummary(8) } : {}),
        'Roll direction': isRollerProduct && config.roll_direction ? getStepSummary(5) : '—',
        Bracket: isRollerProduct && config.bracket_type ? getStepSummary(6) : '—',
        Extras: config.optional_features.length ? config.optional_features.map(id => productData?.optional_features?.find(f => f.id === id)?.name || id).join(', ') : 'None',
        Installation: config.needs_installation ? 'Yes' : 'No',
        Room: config.room_label || '—',
      },
      total: totalValue !== null ? formatPrice(totalValue) : null,
      total_value: totalValue,
    };
    persistCompare([...compareDesigns, snapshot]);
    toast.success(`${selectedColor.name} design saved to compare (${compareDesigns.length + 1}/${MAX_COMPARE})`);
  };
  const loadCompareDesign = (snap) => {
    applySnapshot(snap);
    setOpenSections([]);
    setCompareOpen(false);
    toast.success(`Loaded ${snap.fabric?.name || 'design'}`);
  };

  const buildDesignEmailPayload = async () => {
    let resumeUrl = selectedColor ? `${window.location.origin}/products/${productType}?fabric=${selectedColor.id}` : null;
    try {
      resumeUrl = await createShareLink();
    } catch (e) {
      /* fall back to the fabric deep link */
    }
    const featureNames = config.optional_features
      .map(id => productData?.optional_features?.find(f => f.id === id)?.name)
      .filter(Boolean);
    const summary = {
      Fabric: getStepSummary(1),
      'Mount type': config.mount_type ? getStepSummary(2) : '',
      Size: sizeValid ? `${config.width}" W × ${config.height}" H` : '',
      Quantity: String(config.quantity),
      Control: config.control_type ? getStepSummary(4) : '',
      'Fold style': romanSelected ? getStepSummary(8) : '',
      'Roll direction': isRollerProduct && config.roll_direction ? getStepSummary(5) : '',
      Bracket: isRollerProduct && config.bracket_type ? `${getStepSummary(6)}${config.bracket_color ? ` · ${productData?.bracket_colors?.find(b => b.id === config.bracket_color)?.name || config.bracket_color}` : ''}` : '',
      Installation: config.needs_installation ? 'Professional installation requested' : '',
      Room: config.room_label || '',
    };
    const sceneUrl = currentVisualization?.image_url || null;
    return {
      product_name: productInfo.name,
      product_type: productType,
      summary,
      extras: featureNames,
      total: isConfigReady ? formatPrice(price.total_price || price.total || 0) : null,
      swatch_url: selectedColor?.image_url || null,
      room_scene_url: sceneUrl,
      room_render_url: activeRoomRender?.url || null,
      resume_url: resumeUrl,
    };
  };

  // Accidental double adds are blocked: the same unchanged configuration needs an explicit "Add another".
  const [lastAddedKey, setLastAddedKey] = useState(null);
  const addingRef = useRef(false);
  const handleAddToCart = async (opts) => {
    if (!isConfigReady) {
      toast.error('Please complete all configuration steps first');
      return;
    }
    if (!roomLabelValid) {
      toast.error('Please choose a room for this shade');
      document.querySelector('[data-testid="room-label-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (addingRef.current) return;
    const key = JSON.stringify(cartConfig());
    if (opts?.force !== true && key === lastAddedKey) {
      toast('This shade is already in your cart', {
        description: 'To order more of the same shade, change the quantity or add it again.',
        action: { label: 'Add again', onClick: () => handleAddToCart({ force: true }) },
      });
      return;
    }
    addingRef.current = true;
    try {
      await addToCart(cartConfig(), { openPanel: false });
      setLastAddedKey(key);
      clearConfigDraft(productType);
      setHasDraft(false);
      setSessionShades(prev => [...prev, {
        room: config.room_label.trim(), window: config.window_label?.trim() || '', color: selectedColor?.name || '',
        swatch: selectedColor?.image_url || null, hex: selectedColor?.hex_color || null,
        width: Number(config.width), height: Number(config.height), quantity: config.quantity || 1,
        total: price.total_price || price.total || 0,
        config: cartConfig(),
      }]);
      setAddedPanelOpen(true);
      setOpenSections([]);
      toast.success(`${fullRoomLabel} added to cart!`);
      setTimeout(() => document.querySelector('[data-testid="add-another-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      addingRef.current = false;
    }
  };

  // Duplicate a shade from this session: same config, auto-numbered window name, straight into the cart
  const nextWindowName = (room, base) => {
    const used = new Set(sessionShades.filter(s => s.room === room).map(s => s.window));
    const stem = (base || 'Window').replace(/\s+\d+$/, '');
    let n = 2;
    while (used.has(`${stem} ${n}`)) n += 1;
    return `${stem} ${n}`;
  };
  const duplicateShade = async (index) => {
    const src = sessionShades[index];
    if (!src?.config) return;
    const windowName = nextWindowName(src.room, src.window);
    const label = `${src.room} — ${windowName}`;
    try {
      setDuplicatingIndex(index);
      await addToCart({ ...src.config, room_label: label }, { openPanel: false });
      setSessionShades(prev => [...prev, { ...src, window: windowName, config: { ...src.config, room_label: label } }]);
      toast.success(`Duplicated as ${label}`);
    } catch {
      toast.error('Failed to duplicate shade');
    } finally {
      setDuplicatingIndex(null);
    }
  };

  // "Add another shade" shortcuts — keep fabric & options, vary room / window
  const addAnotherWindow = () => {
    setConfig(prev => ({ ...prev, window_label: '' }));
    setOpenSections([3]);
    setTimeout(() => document.querySelector('[data-testid="step-3-trigger"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
  };
  const addAnotherRoom = () => {
    setConfig(prev => ({ ...prev, room_label: '', window_label: '', width: '', height: '' }));
    setOpenSections([3]);
    setTimeout(() => document.querySelector('[data-testid="step-3-trigger"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
  };

  const handleSaveDesign = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save designs');
      return;
    }
    try {
      await axios.post(`${API_URL}/configurations`, cartConfig(), {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Design saved!');
    } catch (error) {
      toast.error('Failed to save design');
    }
  };

  const selectedMaterial = productData?.materials?.find(m => m.id === config.fabric_material);
  // Get selectedColor from the 4-tier hierarchy (fabric collections) 
  // NOTE: productData.colors uses string slugs ("snow", "ivory") while fabric collections use UUIDs
  // So we only check fabric collections here to avoid ID mismatch
  const colorsSource = selectedFabricMaterial?.colors || selectedCollection?.colors || [];
  const selectedColor = colorsSource.find(c => c.id === config.fabric_color);
  // Preload the open collection's Scenes so color switches are instant
  useEffect(() => (colorsSource.length ? preloadCollectionScenes(colorsSource) : undefined), [colorsSource]); // eslint-disable-line react-hooks/exhaustive-deps
  // Fold cards show the picked color's real AI scene per fold (same room for all, Penthouse first)
  const foldThumbs = (() => {
    if (!romanSelected || !selectedColor || !uiSettings.fold_thumbnails) return null;
    const rv = selectedColor.roman_fold_visualizations || {};
    const maps = Object.fromEntries(ROMAN_FOLDS.map(([id]) => [id, id === 'flat' ? (rv.flat || selectedColor.room_visualizations || {}) : (rv[id] || {})]));
    const rooms = ['penthouse_living', 'master_suite', ...new Set(Object.values(maps).flatMap(m => Object.keys(m)))];
    const room = rooms.find(r => ROMAN_FOLDS.every(([id]) => maps[id][r])) || rooms.find(r => maps.flat[r]);
    const out = Object.fromEntries(ROMAN_FOLDS.map(([id]) => [id, maps[id][room] ? sceneThumb(maps[id][room], 512) : null]).filter(([, u]) => u));
    return Object.keys(out).length ? out : null;
  })();

  // Right after the price reveal: "Which room is this for?" — one tap picks the room and adds to cart
  const [roomPromptOpen, setRoomPromptOpen] = useState(false);
  const [pendingRoomAdd, setPendingRoomAdd] = useState(false);
  const roomPromptShownRef = useRef(false);
  useEffect(() => {
    if (!celebrated || roomLabelValid || roomPromptShownRef.current) return undefined;
    roomPromptShownRef.current = true;
    setPromptAdded(null);
    const t = setTimeout(() => setRoomPromptOpen(true), 1500);
    return () => clearTimeout(t);
  }, [celebrated]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pendingRoomAdd && roomLabelValid) {
      setPendingRoomAdd(false);
      const room = config.room_label.trim();
      handleAddToCart().then(() => setPromptAdded(room));
    }
  }, [pendingRoomAdd, roomLabelValid]); // eslint-disable-line react-hooks/exhaustive-deps
  const [promptAdded, setPromptAdded] = useState(null); // room name once the prompt's shade is in the cart
  const pickRoomAndAdd = (room) => { updateConfig('room_label', room); setPendingRoomAdd(true); };
  const promptSameSize = () => duplicateShade(sessionShades.length - 1);
  const promptDifferentSize = () => { setRoomPromptOpen(false); addAnotherWindow(); };
  const promptCount = promptAdded ? sessionShades.filter(sh => sh.room === promptAdded).length : 0;
  const promptTotal = promptAdded ? sessionShades.filter(sh => sh.room === promptAdded).reduce((n, sh) => n + (Number(sh.total) || 0), 0) : 0;
  const pickOtherRoom = () => {
    setRoomPromptOpen(false);
    setTimeout(() => document.querySelector('[data-testid="room-label-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
  };

  // Finished design: pre-create the share link so "Share your design" can open the phone's share menu instantly
  const [readyLink, setReadyLink] = useState(null);
  const shareKey = isConfigReady && celebrated ? JSON.stringify([config, selectedColor?.id]) : null;
  useEffect(() => {
    setReadyLink(null);
    if (!shareKey) return undefined;
    const t = setTimeout(() => { createShareLink().then(setReadyLink).catch(() => {}); }, 700);
    return () => clearTimeout(t);
  }, [shareKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const shareTitle = selectedColor ? `My ${productInfo.name} · ${selectedColor.name}` : undefined;
  const shareFinished = () => {
    if (readyLink && canNativeShare()) return nativeShare(readyLink, shareTitle).catch(() => {});
    setShareDesignOpen(true);
  };


  
  const selectedBracket = productData?.bracket_colors?.find(b => b.id === config.bracket_color);
  const hasValance = config.optional_features.some(f => f.includes('valance'));

  // Find matching visualization when config changes
  // Only color-faithful matches are used: 1. the color's own AI room scene, 2. exact color_id, 3. color name. Otherwise the CSS simulator renders the real swatch.
  useEffect(() => {
    if (!config.fabric_color || !selectedColor) {
      setCurrentVisualization(null);
      return;
    }
    const ownScene = selectedColor.room_visualizations?.[selectedColor.default_room]
      || selectedColor.room_visualizations?.living_room
      || selectedColor.room_visualization_url
      || selectedColor.ai_visualization_url
      || Object.values(selectedColor.room_visualizations || {})[0];
    if (ownScene) {
      setCurrentVisualization({
        id: `color-scene-${selectedColor.id}`,
        color_id: selectedColor.id,
        ai_generated: true,
        image_url: ownScene,
        title: `${selectedColor.name} — ${selectedCollection?.name || ''} room view`.trim()
      });
      return;
    }
    let match = visualizations.find(v => v.color_id === config.fabric_color && v.ai_generated)
      || visualizations.find(v => v.color_id === config.fabric_color);
    if (!match && selectedColor.name) {
      match = visualizations.find(v => (v.fabric_color === selectedColor.name || v.color_name === selectedColor.name) && v.ai_generated)
        || visualizations.find(v => v.fabric_color === selectedColor.name || v.color_name === selectedColor.name);
    }
    setCurrentVisualization(match || null);
  }, [visualizations, config.fabric_color, selectedColor, selectedCollection?.name]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  // Product Info Page (Step 0)
  if (!showConfigurator) {
    // Get CMS data with fallbacks to PRODUCT_INFO
    const heroImage = cmsProduct?.images?.find(img => img.is_primary)?.url || cmsProduct?.images?.[0]?.url || productInfo.heroImage;
    const displayName = cmsProduct?.display_name || cmsProduct?.title || productInfo.name;
    const heroText = cmsProduct?.hero_text || cmsProduct?.description || productInfo.description;
    const ctaText = cmsProduct?.cta_text || 'Configure Now';
    const features = cmsProduct?.features || [];
    
    return (
      <Layout>
        {/* Hero - Using CMS Data */}
        <section className="relative min-h-[70vh] flex items-center">
          <div className="absolute inset-0">
            <img 
              src={heroImage.startsWith('/') ? `${API_URL.replace('/api', '')}${heroImage}` : heroImage} 
              alt={displayName} 
              className="w-full h-full object-cover" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 via-neutral-900/50 to-transparent" />
            {/* Phones: the text spans the full width, so darken the whole image behind it */}
            <div className="absolute inset-0 md:hidden bg-gradient-to-b from-neutral-900/40 via-neutral-900/60 to-neutral-900/75" data-testid="hero-mobile-overlay" />
          </div>
          <div className="container-xl relative z-10 pt-20">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl [text-shadow:0_1px_12px_rgba(0,0,0,0.35)] md:[text-shadow:none]">
              <Breadcrumbs light className="mb-6" />
              <h1 className="text-white mb-6">{displayName}</h1>
              <div 
                className="text-xl text-neutral-300 mb-8 prose prose-lg prose-invert max-w-none [&>p]:my-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(heroText || '') }}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={() => setShowConfigurator(true)} className="btn-primary !bg-white !text-neutral-900 hover:!bg-neutral-100" data-testid="start-configurator-btn">
                  {ctaText} <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
                {hasDraft && (
                  <Button onClick={() => setShowConfigurator(true)} variant="outline" className="border-white/60 text-white hover:bg-white/10 h-12" data-testid="resume-configurator-btn">
                    <History className="w-4 h-4 mr-2" /> Resume where you left off
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features from CMS */}
        {features.length > 0 && (
          <section className="section-padding">
            <div className="container-xl">
              <div className="text-center mb-12">
                <span className="label-overline text-amber-700">Features</span>
                <h2 className="mt-4">{cmsProduct?.features_section_title || 'What Makes It Special'}</h2>
              </div>
              <div className={`grid gap-6 ${features.slice(0, 3).length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'}`} data-testid="product-features-grid">
                {features.slice(0, 3).map((feature, idx) => (
                  <div key={idx} className="p-8 border border-neutral-200 text-center">
                    {feature.image_url ? (
                      <img 
                        src={feature.image_url.startsWith('/') ? `${API_URL.replace('/api', '')}${feature.image_url}` : feature.image_url}
                        alt={feature.title}
                        className="w-16 h-16 mx-auto mb-4 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 mx-auto mb-4 text-amber-700 flex items-center justify-center">
                        <Check className="w-6 h-6" />
                      </div>
                    )}
                    <h4 className="text-xl mb-2">{feature.title}</h4>
                    <p className="text-neutral-600 text-sm whitespace-pre-line">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Operating Systems - From CMS or Defaults (motor pages are motorized only, so manual controls are not offered there) */}
        {!isMotorPage && (
        <section className="section-padding bg-neutral-50" data-testid="operating-systems-section">
          <div className="container-xl">
            <div className="text-center mb-12">
              <span className="label-overline text-amber-700">Operating Systems</span>
              <h2 className="mt-4">{cmsProduct?.operating_systems_title || 'Choose Your Control Type'}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {(cmsProduct?.operating_systems?.length > 0 ? cmsProduct.operating_systems : [
                { id: 'motorized', icon: 'Zap', title: 'Motorized', description: 'Remote, app, or voice control with smart home integration. Whisper-quiet motors.' },
                { id: 'continuous-loop', icon: 'RotateCcw', title: 'Continuous Loop', description: 'Smooth chain operation for precise positioning. Clean, modern look.' },
                { id: 'cordless', icon: 'Hand', title: 'Cordless', description: 'Child-safe operation with gentle push/pull. No cords or chains.' },
              ]).map((control) => {
                // Map icon names to components
                const IconComponent = control.icon === 'Zap' ? Zap : control.icon === 'RotateCcw' ? RotateCcw : control.icon === 'Hand' ? Hand : Zap;
                return (
                  <div key={control.id || control.title} className="p-8 border border-neutral-200 text-center bg-white">
                    {control.image_url ? (
                      <img 
                        src={control.image_url.startsWith('/') ? `${API_URL.replace('/api', '')}${control.image_url}` : control.image_url}
                        alt={control.title}
                        className="w-16 h-16 mx-auto mb-4 rounded-lg object-cover"
                      />
                    ) : (
                      <IconComponent className="w-10 h-10 mx-auto mb-4 text-amber-700" strokeWidth={1.5} />
                    )}
                    <h4 className="text-xl mb-2">{control.title}</h4>
                    <p className="text-neutral-600 text-sm whitespace-pre-line">{control.description || control.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {/* Benefits - From CMS or Defaults */}
        <section className="section-padding">
          <div className="container-xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="label-overline text-amber-700">Benefits</span>
                <h2 className="mt-4 mb-6">{cmsProduct?.benefits_section_title || `Why Choose ${displayName}?`}</h2>
                <ul className="space-y-4">
                  {(cmsProduct?.benefits?.length > 0 ? cmsProduct.benefits : [
                    { text: 'Clean, modern aesthetic' }, 
                    { text: 'Privacy and light control' }, 
                    { text: 'Wide fabric selection' }, 
                    { text: 'Ideal for homes and commercial spaces' }, 
                    { text: 'Smart home integration available' }
                  ]).map((benefit, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-neutral-600">
                      {benefit.image_url ? (
                        <img 
                          src={benefit.image_url.startsWith('/') ? `${API_URL.replace('/api', '')}${benefit.image_url}` : benefit.image_url}
                          alt=""
                          className="w-8 h-8 rounded object-cover flex-shrink-0"
                        />
                      ) : (
                        <Check className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      )}
                      {benefit.text || benefit}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => setShowConfigurator(true)} className="btn-primary mt-8">
                  Start Configuring
                </Button>
              </div>
              <div>
                <img 
                  src={cmsProduct?.benefits_image_url 
                    ? (cmsProduct.benefits_image_url.startsWith('/') ? `${API_URL.replace('/api', '')}${cmsProduct.benefits_image_url}` : cmsProduct.benefits_image_url)
                    : heroImage
                  } 
                  alt={`${displayName} benefits`}
                  data-testid="product-benefits-image" 
                  className="w-full aspect-[4/3] object-cover rounded-lg" 
                />
              </div>
            </div>
          </div>
        </section>
        <ProductCmsContent slug={productType} />
        <ProductNextSteps slug={productType} name={displayName.toLowerCase()} />
      </Layout>
    );
  }

  // Configurator (Steps 1-7)
  return (
    <Layout hideFooter>
      <div className="min-h-screen flex justify-center pt-20 bg-neutral-100" data-testid="configurator-page">
        <div className="grid lg:grid-cols-2 w-full max-w-7xl">
        {/* Left Panel - Realistic Shade Simulator */}
        <div className="hidden lg:flex sticky top-20 h-[calc(100vh-5rem)] items-center justify-center p-6 relative overflow-hidden bg-neutral-100">
          <div className="relative w-full max-w-lg" data-testid="shade-preview">
            {/* Shade Visualizer - Extracted Component */}
            <SharePreview selectedColor={selectedColor} productType={productType} config={config} hideBar={isVisualizerFullscreen}>
            <div className="relative rounded-xl overflow-hidden shadow-2xl">
              <ShadeVisualizer
                onChooseFabric={() => jumpToStep(1)}
                config={config}
                selectedColor={selectedColor}
                selectedCollection={selectedCollection}
                selectedMaterial={selectedFabricMaterial}
                productData={productData}
                productType={productType}
                currentVisualization={currentVisualization}
                colorVisualizationImages={COLOR_VISUALIZATION_IMAGES}
                selectedOpenness={config.selected_openness}
                onFullscreenChange={setIsVisualizerFullscreen}
                onVisualizationApproved={(approvalData) => {
                  // approvalData = { url, shade_regions, room_type } from the approval response
                  const newUrl = typeof approvalData === 'string' ? approvalData : approvalData?.url;
                  const shadeRegions = typeof approvalData === 'object' ? approvalData?.shade_regions : null;
                  const roomType = typeof approvalData === 'object' ? (approvalData?.room_type || 'living_room') : 'living_room';

                  if (newUrl && selectedColor) {
                    if (!selectedColor.room_visualizations) selectedColor.room_visualizations = {};
                    selectedColor.room_visualizations[roomType] = newUrl;
                    selectedColor.room_visualization_url = newUrl;
                    selectedColor.ai_visualization_url = newUrl;
                    if (approvalData?.make_default) selectedColor.default_room = roomType;

                    // If the backend returned shade_regions from synchronous detection,
                    // apply them immediately so openness compositing works without page reload.
                    if (shadeRegions?.polygons?.length > 0) {
                      if (!selectedColor.shade_regions) selectedColor.shade_regions = {};
                      selectedColor.shade_regions[roomType] = {
                        polygons: shadeRegions.polygons,
                        clean_url: shadeRegions.clean_url || null,
                      };
                    }

                    const hasValidRegions = shadeRegions?.polygons?.length > 0 && !!shadeRegions?.clean_url;
                    setCurrentVisualization({
                      id: `color-scene-${selectedColor.id}-regen`,
                      color_id: selectedColor.id,
                      ai_generated: true,
                      image_url: newUrl,
                      // Only skip compositing if we don't have valid regions back from the backend.
                      // When regions are returned, compositing works immediately.
                      skipComposite: !hasValidRegions,
                      title: `${selectedColor.name} — ${selectedCollection?.name || ''} room view`.trim()
                    });
                  }
                }}
              />
            </div>
            </SharePreview>
            {romanSelected && selectedColor && (
              <button type="button" onClick={() => setCompareFoldsOpen(true)} data-testid="preview-compare-folds-btn"
                className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg border border-neutral-200 bg-white py-2 text-xs font-medium text-neutral-700 hover:border-neutral-900 transition-colors">
                Compare fold styles
              </button>
            )}
            <CompareFoldsDialog open={compareFoldsOpen} onOpenChange={setCompareFoldsOpen} selectedColor={selectedColor} selectedCollection={selectedCollection}
              selectedMaterial={selectedFabricMaterial} config={config} currentFold={priceIdToFold(config.fold_style)} onChoose={(f) => updateConfig('fold_style', FOLD_TO_PRICE_ID[f])} />
            
            {/* Info bar below preview - Shows selected fabric details */}
            <div className="mt-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-neutral-200">
              {/* Fabric Selection Summary */}
              <div className="flex items-start gap-3 mb-3">
                <div 
                  className="w-12 h-12 rounded-lg shadow-inner border border-neutral-200 flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: selectedColor?.hex_color || selectedColor?.hex || '#f5f5f4' }}
                >
                  {selectedColor?.image_url && (
                    <img src={resolveImageUrl(selectedColor.image_url, 128)} alt={selectedColor.name} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-neutral-900 font-semibold text-base truncate">
                    {selectedColor?.name || 'Select a Color'}
                  </p>
                  <p className="text-neutral-600 text-sm">
                    {/* Show full path matching the Fabric step header */}
                    {selectedBrand && selectedCollection && selectedColor ? (
                      selectedFabricMaterial ? (
                        // 4-tier: Brand → Collection → Material → Color
                        <>{brandLabel(selectedBrand.name)} → {selectedCollection.name} → {selectedFabricMaterial.name} → {selectedColor.name}</>
                      ) : (
                        // 3-tier: Brand → Collection → Color
                        <>{brandLabel(selectedBrand.name)} → {selectedCollection.name} → {selectedColor.name}</>
                      )
                    ) : selectedBrand && selectedCollection ? (
                      // Partial selection (no color yet)
                      selectedFabricMaterial ? (
                        <>{brandLabel(selectedBrand.name)} → {selectedCollection.name} → {selectedFabricMaterial.name}</>
                      ) : (
                        <>{brandLabel(selectedBrand.name)} → {selectedCollection.name}</>
                      )
                    ) : selectedMaterial?.name ? (
                      selectedMaterial.name
                    ) : (
                      'Select fabric from the options'
                    )}
                  </p>
                  {selectedColor?.code && (
                    <p className="text-neutral-400 text-xs mt-0.5">Code: {selectedColor.code}</p>
                  )}
                  {activeRoomRender && (
                    <p className="text-emerald-700 text-xs mt-1 flex items-center gap-1" data-testid="room-render-ready-hint">
                      <ImagePlus className="w-3 h-3" /> Your room render is ready — it will be included when you email this design.
                    </p>
                  )}
                </div>
              </div>
              
              {/* Size & Actions Row */}
              <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
                <div>
                  <p className="text-neutral-900 font-mono text-lg" data-testid="preview-size-display">
                    {config.width && config.height ? `${config.width}" × ${config.height}"` : 'Size not set'}
                  </p>
                  <p className="text-neutral-500 text-xs">{config.room_label || (config.width && config.height ? 'Custom Size' : 'Enter width & height in step 3')}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setRoomRenderOpen(true)}
                    disabled={!selectedColor}
                    variant="outline"
                    className="text-sm border-neutral-300"
                    data-testid="room-photo-preview-button"
                  >
                    <ImagePlus className="w-4 h-4 mr-2" />
                    See it in your room
                  </Button>
                  <Button
                    onClick={() => setShowARViewer(true)}
                    disabled={!config.fabric_color || !sizeValid}
                  variant="outline"
                  className="bg-amber-600 border-amber-600 text-white hover:bg-amber-700 text-sm"
                  data-testid="ar-preview-button-panel"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Try AR
                </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Room Photo Preview - shopper uploads their own window photo */}
        <RoomRenderDialog
          open={roomRenderOpen}
          onOpenChange={setRoomRenderOpen}
          entry={selectedColor ? { id: selectedColor.id, name: selectedColor.name } : null}
          onRendered={setRoomRenderResult}
        />
        <EmailDesignDialog
          open={emailDesignOpen}
          onOpenChange={setEmailDesignOpen}
          buildPayload={buildDesignEmailPayload}
          hasRoomRender={Boolean(activeRoomRender)}
        />
        <RoomPromptDialog
          open={roomPromptOpen}
          onOpenChange={setRoomPromptOpen}
          price={isConfigReady ? formatPrice(price.total_price || price.total || 0) : null}
          onPick={pickRoomAndAdd}
          onOther={pickOtherRoom}
          added={promptAdded}
          addedCount={promptCount}
          addedTotal={promptTotal ? formatPrice(promptTotal) : null}
          busy={pendingRoomAdd || duplicatingIndex !== null}
          onSameSize={promptSameSize}
          onDifferentSize={promptDifferentSize}
          sizeLabel={sizeValid ? `${config.width}" × ${config.height}"` : ''}
        />
        <ShareDesignDialog
          open={shareDesignOpen}
          onOpenChange={setShareDesignOpen}
          createLink={createShareLink}
          shareTitle={shareTitle}
          onEmail={() => { setShareDesignOpen(false); setEmailDesignOpen(true); }}
        />
        <CompareDesignsDialog
          open={compareOpen}
          onOpenChange={setCompareOpen}
          designs={compareDesigns}
          canAddCurrent={Boolean(selectedColor) && compareDesigns.length < MAX_COMPARE}
          onAddCurrent={addCurrentToCompare}
          onLoad={loadCompareDesign}
          onRemove={(id) => persistCompare(compareDesigns.filter(d => d.id !== id))}
        />
        
        {/* AR Viewer Modal */}
        <AnimatePresence>
          {showARViewer && (
            <ARViewer 
              config={config}
              productType={productInfo.previewType}
              selectedColor={selectedColor}
              onClose={() => setShowARViewer(false)}
            />
          )}
        </AnimatePresence>

        {/* Right Panel - Dropdown Sections */}
        <div className="bg-white min-h-screen flex flex-col min-w-0">
          <div className="p-6 lg:p-8 flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
              <div className="min-w-0">
                <h1 className="text-2xl font-serif text-neutral-900">Configure Your {productType === 'solar-shades' ? 'Solar Shades' : productInfo.name}</h1>
                <p className="text-neutral-500 mt-1">Customize each option below</p>
              </div>
              <div className="flex items-center gap-1 -ml-3 sm:ml-0">
                {(hasDraft || draftHasSelections(config)) && (
                  <Button variant="ghost" onClick={startOver} className="text-neutral-500" data-testid="start-over-button">
                    <RotateCcw className="w-4 h-4 mr-2" /> Start over
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setShowConfigurator(false)} className="text-neutral-500">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </div>
            </div>

            {hasDraft && (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="draft-restored-banner">
                <p className="text-sm text-amber-900 flex items-center gap-2">
                  <History className="w-4 h-4 text-amber-600" /> Welcome back — we restored your saved progress.
                </p>
                <button onClick={() => setHasDraft(false)} className="text-amber-700 hover:text-amber-900" aria-label="Dismiss" data-testid="draft-banner-dismiss">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <MobilePreviewPanel
              onChooseFabric={() => jumpToStep(1)}
              config={config}
              selectedColor={selectedColor}
              selectedBrand={selectedBrand}
              selectedCollection={selectedCollection}
              selectedFabricMaterial={selectedFabricMaterial}
              productData={productData}
              productType={productType}
              currentVisualization={currentVisualization}
              colorVisualizationImages={COLOR_VISUALIZATION_IMAGES}
              price={price}
              isConfigReady={isConfigReady}
              sizeValid={sizeValid}
              onRoomPhoto={() => setRoomRenderOpen(true)}
              onAR={() => setShowARViewer(true)}
              onEmail={() => setEmailDesignOpen(true)}
            />

            <ConfiguratorProgress
              steps={[...stepOrder.map(id => ({ id, title: stepTitles[id], complete: isStepComplete(id) || (id === 7 && isConfigReady) })), { id: 'room', title: 'Room', complete: roomLabelValid }]}
              openSections={openSections}
              onJump={(id) => (id === 'room' ? document.querySelector('[data-testid="room-label-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }) : jumpToStep(id))}
              fabric={selectedColor ? { brand: brandLabel(selectedBrand?.name), collection: selectedCollection?.name, material: selectedFabricMaterial?.name, color: selectedColor } : null}
              onChangeFabric={() => jumpToStep(1)}
              estimate={{ amount: price.total_price || price.total || 0, isEstimate: !isConfigReady }}
              roomMissing={!roomLabelValid}
            />

            {editingCartItem && (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3" data-testid="editing-cart-item-banner">
                <p className="text-sm text-sky-900 flex items-center gap-2 min-w-0">
                  <Pencil className="w-4 h-4 text-sky-600 shrink-0" />
                  <span className="truncate">Editing <strong>{editingCartItem.label}</strong> from your cart — changes replace that item.</span>
                </p>
                <button onClick={cancelEditCartItem} className="text-xs text-sky-800 hover:underline shrink-0" data-testid="editing-cart-item-cancel">Cancel edit</button>
              </div>
            )}

            {addedPanelOpen && (
              <AddAnotherShadePanel
                shades={sessionShades}
                lastRoom={sessionShades[sessionShades.length - 1]?.room}
                cartCount={itemCount}
                onSameRoom={() => { setAddedPanelOpen(false); addAnotherWindow(); }}
                onNewRoom={() => { setAddedPanelOpen(false); addAnotherRoom(); }}
                onStartFresh={() => { setAddedPanelOpen(false); startOver(); }}
                onClose={() => setAddedPanelOpen(false)}
                onViewCart={() => setCartOpen(true)}
                onDuplicate={duplicateShade}
                duplicatingIndex={duplicatingIndex}
                onRemoveShade={(i) => setSessionShades(prev => prev.filter((_, idx) => idx !== i))}
                onClearShades={() => { setSessionShades([]); }}
              />
            )}

            <div className="space-y-3">
              {/* Section 1: Fabric */}
              <Collapsible open={openSections.includes(1)} onOpenChange={() => toggleSection(1)}>
                <CollapsibleTrigger className="w-full" data-testid="step-1-trigger">
                  <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    openSections.includes(1) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isStepComplete(1) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                      }`}>
                        {isStepComplete(1) ? <Check className="w-4 h-4" /> : '1'}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-neutral-900">Fabric</p>
                        <p className="text-sm text-neutral-500">{getStepSummary(1)}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(1) ? 'rotate-180' : ''}`} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-4 pb-2 px-2" data-testid="step-1-content">
                    {/* Brand & Collection Selection with Horizontal Tabs */}
                    {fabricCollections.length > 0 && (
                      <div className="space-y-4">
                        {/* STEP 1: Brand Selection - Horizontal Tabs */}
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider block mb-2">Select Brand</Label>
                          <div className="flex gap-2 overflow-x-auto pb-2">
                            {fabricCollections.map((brand) => (
                              <button
                                key={brand.id}
                                onClick={() => {
                                  setSelectedBrand(brand);
                                  setSelectedCollection(null);
                                  setSelectedFabricMaterial(null);
                                  setConfig(prev => ({ ...prev, fabric_color: '', fabric_material: '', fabric_brand: brand.id, fabric_collection: undefined }));
                                }}
                                className={`px-4 py-2 rounded-lg border-2 whitespace-nowrap font-medium text-sm transition-all ${
                                  selectedBrand?.id === brand.id 
                                    ? 'border-amber-500 bg-amber-50 text-amber-800' 
                                    : 'border-neutral-200 hover:border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                                }`}
                                data-testid={`brand-tab-${brand.id}`}
                              >
                                {brandLabel(brand.name)}
                                <span className="ml-1 text-xs opacity-60">({brand.collections?.length || 0})</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* STEP 2 + 3: Type (collection) accordion — the Collections (materials) open right under the selected Type */}
                        {selectedBrand?.collections?.length > 0 && (() => {
                          const L = tierLabels(selectedBrand?.name);
                          const colorStep = (selectedFabricMaterial?.colors || selectedCollection?.colors || []).length > 0 ? (
                          <motion.div 
                            initial={{ opacity: 0, y: 10 }} 
                            animate={{ opacity: 1, y: 0 }}
                            className="pt-4 border-t border-neutral-200"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                              <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Color</Label>
                              <div className="flex flex-wrap items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setFabricGalleryOpen(true)}
                                  className="h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                                  data-testid="browse-all-fabrics-btn"
                                >
                                  <LayoutGrid className="w-3 h-3" />
                                  Browse all fabrics
                                </Button>
                                {selectedSampleColors.length > 0 && (
                                  <span className="text-xs text-amber-600 font-medium">
                                    {selectedSampleColors.length} selected for samples
                                  </span>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // If no colors selected but a color is in the config, auto-add it
                                    if (selectedSampleColors.length === 0 && config.fabric_color) {
                                      const currentColor = (selectedFabricMaterial?.colors || selectedCollection?.colors || []).find(c => c.id === config.fabric_color);
                                      if (currentColor) {
                                        toggleSampleColor(currentColor);
                                        toast.success(`Added ${currentColor.name} to sample request`);
                                      }
                                    }
                                    setSampleModalOpen(true);
                                  }}
                                  className="h-7 text-xs gap-1"
                                  data-testid="request-samples-btn"
                                >
                                  <Package className="w-3 h-3" />
                                  Request Samples {selectedSampleColors.length > 0 && `(${selectedSampleColors.length})`}
                                </Button>
                              </div>
                            </div>
                            <p className="text-xs text-neutral-400 mb-2">Click to select color, hover for zoom option</p>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {(selectedFabricMaterial?.colors || selectedCollection?.colors || []).map((color) => (
                                <TooltipProvider key={color.id}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="relative group">
                                        <button
                                          onClick={() => {
                                            // Click to SELECT the color
                                            updateConfig('fabric_color', color.id);
                                          }}
                                          className={`relative block aspect-square rounded-lg border-2 overflow-hidden transition-all w-full ${
                                            config.fabric_color === color.id 
                                              ? 'border-neutral-900 ring-2 ring-amber-400 scale-105' 
                                              : 'border-transparent hover:scale-105'
                                          } ${isColorSelectedForSample(color.id) ? 'ring-2 ring-green-500' : ''}`}
                                          data-testid={`color-${color.id}`}
                                        >
                                          {color.image_url ? (
                                            <img 
                                              src={resolveImageUrl(color.image_url, 128)} 
                                              alt={color.name}
                                              loading="lazy"
                                              className="absolute inset-0 w-full h-full object-cover"
                                            />
                                          ) : (
                                            <div 
                                              className="absolute inset-0"
                                              style={{ backgroundColor: color.hex_color || '#E5E5E5' }}
                                            />
                                          )}
                                          {color.is_on_sale && (
                                            <span className="absolute top-0 right-0 bg-red-500 text-white text-[8px] px-1 font-bold">SALE</span>
                                          )}
                                          {/* Selected checkmark */}
                                          {config.fabric_color === color.id && (
                                            <div className="absolute bottom-1 right-1 w-4 h-4 bg-amber-600 rounded-full flex items-center justify-center">
                                              <Check className="w-3 h-3 text-white" />
                                            </div>
                                          )}
                                        </button>
                                        {/* Zoom button - visible on hover, positioned in top-left corner */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openColorZoom(color);
                                          }}
                                          className="absolute top-1 left-1 w-5 h-5 rounded bg-black/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                                          title="View details"
                                        >
                                          <ZoomIn className="w-3 h-3" />
                                        </button>
                                        {/* Sample selection checkbox - visible on hover or when selected */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSampleColor(color);
                                          }}
                                          className={`absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs transition-all ${
                                            isColorSelectedForSample(color.id)
                                              ? 'bg-green-500 border-green-500 text-white opacity-100'
                                              : 'bg-white border-neutral-300 text-neutral-400 hover:border-green-400 opacity-0 group-hover:opacity-100'
                                          }`}
                                          title={isColorSelectedForSample(color.id) ? 'Remove from samples' : 'Add to sample request'}
                                        >
                                          {isColorSelectedForSample(color.id) ? '✓' : '+'}
                                        </button>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="font-medium">{color.name}</p>
                                      {color.code && <p className="text-xs opacity-75">Code: {color.code}</p>}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ))}
                            </div>
                            {config.fabric_color ? (
                              <p className="text-xs text-neutral-500 mt-2" data-testid="selected-color-caption">
                                Selected: {(selectedFabricMaterial?.colors || selectedCollection?.colors || []).find(c => c.id === config.fabric_color)?.name || 'Unknown'}
                              </p>
                            ) : (
                              <p className="text-xs text-amber-700 mt-2" data-testid="select-color-hint">Select a color to continue</p>
                            )}
                            
                            {/* Large Fabric/Color Preview Section with Technical Specs */}
                            {selectedColor && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }} 
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-4 pt-4 border-t border-neutral-200"
                              >
                                <div className="flex flex-col sm:flex-row gap-4">
                                  {/* Large Fabric Image - Much bigger now */}
                                  <div className="w-full sm:w-48 h-48 rounded-xl overflow-hidden border-2 border-neutral-200 shadow-md flex-shrink-0 relative group">
                                    {selectedColor.image_url ? (
                                      <img 
                                        src={resolveImageUrl(selectedColor.image_url, 512)} 
                                        alt={selectedColor.name}
                                        className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
                                        onClick={() => openColorZoom(selectedColor)}
                                      />
                                    ) : (
                                      <div 
                                        className="w-full h-full flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: selectedColor.hex_color || '#E5E5E5' }}
                                        onClick={() => openColorZoom(selectedColor)}
                                      >
                                        <span className="text-sm text-neutral-500 text-center px-4 font-medium">
                                          {selectedColor.name}
                                        </span>
                                      </div>
                                    )}
                                    {/* Zoom overlay on hover */}
                                    <div 
                                      className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
                                      onClick={() => openColorZoom(selectedColor)}
                                    >
                                      <div className="bg-white/90 rounded-full p-2">
                                        <ZoomIn className="w-5 h-5 text-neutral-700" />
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Color Info & Technical Specifications */}
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-neutral-900 text-lg">{selectedColor.name}</h4>
                                    {selectedColor.code && (
                                      <p className="text-sm text-neutral-500 mt-0.5">Code: {selectedColor.code}</p>
                                    )}
                                    {selectedColor.hex_color && (
                                      <div className="flex items-center gap-2 mt-2">
                                        <div 
                                          className="w-6 h-6 rounded-md border border-neutral-300 shadow-sm" 
                                          style={{ backgroundColor: selectedColor.hex_color }}
                                        />
                                        <span className="text-sm text-neutral-500 font-mono">{selectedColor.hex_color}</span>
                                      </div>
                                    )}
                                    
                                    {/* Technical Specifications Panel */}
                                    <div className="mt-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                                      <h5 className="text-xs font-semibold text-neutral-700 uppercase tracking-wider mb-2">Technical Specs</h5>
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                                        {/* Fabric Type */}
                                        {(selectedCollection?.fabric_type || selectedBrand?.fabric_type) && (
                                          <>
                                            <span className="text-neutral-500">Fabric Type:</span>
                                            <span className="text-neutral-800 font-medium" data-testid="spec-fabric-type">{selectedCollection?.fabric_type || selectedBrand?.fabric_type}</span>
                                          </>
                                        )}
                                        
                                        {/* Openness */}
                                        {(selectedColor?.openness || selectedFabricMaterial?.openness || selectedCollection?.openness || selectedBrand?.openness) && (() => {
                                          const raw = selectedColor?.openness || selectedFabricMaterial?.openness || selectedCollection?.openness || selectedBrand?.openness || '';
                                          const parts = raw.split(',').map(s => s.trim()).filter(Boolean);
                                          const percentParts = parts.filter(p => p.includes('%'));
                                          return (
                                            <>
                                              <span className="text-neutral-500">Openness:</span>
                                              {percentParts.length > 1 ? (
                                                <span className="flex flex-wrap gap-1" data-testid="spec-openness">
                                                  {parts.map(p => {
                                                    const isPct = p.includes('%');
                                                    const isActive = config.selected_openness === p;
                                                    return (
                                                      <button
                                                        key={p}
                                                        type="button"
                                                        onClick={() => isPct ? updateConfig('selected_openness', isActive ? '' : p) : null}
                                                        className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                                                          isActive
                                                            ? 'bg-amber-600 text-white border-amber-600'
                                                            : isPct
                                                              ? 'bg-white text-neutral-700 border-neutral-300 hover:border-amber-400 cursor-pointer'
                                                              : 'bg-neutral-100 text-neutral-500 border-transparent cursor-default'
                                                        }`}
                                                        data-testid={`openness-chip-${p.replace(/[^a-z0-9]/gi, '')}`}
                                                      >
                                                        {p}
                                                      </button>
                                                    );
                                                  })}
                                                  {productType === 'solar-shades' && <OpennessCompareDialog />}
                                                </span>
                                              ) : (
                                                <span className="text-neutral-800 font-medium" data-testid="spec-openness">{raw}</span>
                                              )}
                                            </>
                                          );
                                        })()}
                                        
                                        {/* Composition */}
                                        {(selectedFabricMaterial?.composition || selectedBrand?.composition) && (
                                          <>
                                            <span className="text-neutral-500">Composition:</span>
                                            <span className="text-neutral-800 font-medium">{selectedFabricMaterial?.composition || selectedBrand?.composition}</span>
                                          </>
                                        )}
                                        
                                        {/* Fire Rating */}
                                        {(selectedFabricMaterial?.fire_rating || selectedBrand?.fire_rating) && (
                                          <>
                                            <span className="text-neutral-500">Fire Rating:</span>
                                            <span className="text-neutral-800 font-medium">{selectedFabricMaterial?.fire_rating || selectedBrand?.fire_rating}</span>
                                          </>
                                        )}
                                        
                                        {/* Width */}
                                        {(selectedFabricMaterial?.width || selectedBrand?.width) && (
                                          <>
                                            <span className="text-neutral-500">Max Width:</span>
                                            <span className="text-neutral-800 font-medium">{selectedFabricMaterial?.width || selectedBrand?.width}</span>
                                          </>
                                        )}
                                        
                                        {/* Care Instructions */}
                                        {(selectedFabricMaterial?.care_instructions || selectedBrand?.care_instructions) && (
                                          <>
                                            <span className="text-neutral-500 col-span-2 mt-1">Care:</span>
                                            <span className="text-neutral-600 col-span-2 text-[11px] leading-relaxed">
                                              {selectedFabricMaterial?.care_instructions || selectedBrand?.care_instructions}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      
                                      {/* Fallback message if no specs available */}
                                      {!selectedCollection?.fabric_type && !selectedBrand?.fabric_type && !selectedCollection?.openness && !selectedBrand?.openness && !selectedFabricMaterial?.composition && (
                                        <p className="text-neutral-400 text-xs italic">Technical specifications coming soon</p>
                                      )}
                                    </div>
                                    
                                    {/* Price info if available */}
                                    {selectedColor.price_modifier && selectedColor.price_modifier !== 0 && (
                                      <p className="text-sm text-amber-600 mt-3 font-medium">
                                        Price modifier: {selectedColor.price_modifier > 0 ? '+' : ''}{formatPrice(selectedColor.price_modifier)}
                                      </p>
                                    )}
                                    
                                    {/* Actions */}
                                    <div className="flex gap-3 mt-3">
                                      <button
                                        onClick={() => setRoomRenderOpen(true)}
                                        className="text-sm text-neutral-600 hover:text-neutral-900 underline flex items-center gap-1"
                                        data-testid="room-photo-preview-link"
                                      >
                                        <ImagePlus className="w-3.5 h-3.5" /> See it in your room
                                      </button>
                                      {!isColorSelectedForSample(selectedColor.id) && (
                                        <button
                                          onClick={() => {
                                            toggleSampleColor(selectedColor);
                                            toast.success(`Added ${selectedColor.name} to sample request`);
                                          }}
                                          className="text-sm text-amber-600 hover:text-amber-700 underline"
                                        >
                                          + Add to samples
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                            {renderStepFooter(1)}
                          </motion.div>
                          ) : null;
                          const renderMaterials = (collection) => (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden" data-testid={`collection-materials-${collection.id}`}>
                              <div className="px-3 pb-3 pt-1 border-t border-amber-200/60">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">{L.tier3} · pick one</Label>
                                  {materialsToCompare.length >= 2 && (
                                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setCompareModalOpen(true); }} className="h-7 text-xs gap-1">
                                      <Scale className="w-3 h-3" />Compare ({materialsToCompare.length})
                                    </Button>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {collection.materials.map((material) => (
                                    <div key={material.id} className="relative group">
                                      <CollectionHoverPreview material={material} label={L.tier3}>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedFabricMaterial(material); setConfig(prev => ({ ...prev, fabric_color: '', fabric_material: material.id, selected_openness: '' })); }}
                                        className={`px-3 py-2 border rounded-lg text-sm transition-all ${selectedFabricMaterial?.id === material.id ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-200 bg-white hover:border-neutral-400 text-neutral-700'} ${isMaterialInComparison(material.id) ? 'ring-2 ring-amber-400' : ''}`}
                                        data-testid={`material-${material.id}`}
                                      >
                                        <span className="inline-flex items-center gap-2">
                                          {material.name}
                                          <span className={`text-xs ${selectedFabricMaterial?.id === material.id ? 'text-neutral-300' : 'text-neutral-400'}`}>({material.colors?.length || 0})</span>
                                        </span>
                                      </button>
                                      </CollectionHoverPreview>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); toggleMaterialComparison(material, selectedBrand, collection); }}
                                        className={`absolute -top-1 -right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs transition-all ${isMaterialInComparison(material.id) ? 'bg-amber-500 border-amber-500 text-white opacity-100' : 'bg-white border-neutral-300 text-neutral-400 hover:border-amber-400 opacity-0 group-hover:opacity-100'}`}
                                        title={isMaterialInComparison(material.id) ? 'Remove from comparison' : 'Add to comparison'}
                                      >
                                        {isMaterialInComparison(material.id) ? '✓' : '+'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                {selectedFabricMaterial?.description && <p className="text-xs text-neutral-500 mt-2">{selectedFabricMaterial.description}</p>}
                                {selectedFabricMaterial?.composition && <p className="text-xs text-amber-600 mt-1">Composition: {selectedFabricMaterial.composition}</p>}
                                {selectedFabricMaterial && collection.materials.some(m => m.id === selectedFabricMaterial.id) && (
                                  <div className="mt-3 bg-white rounded-lg border border-neutral-200 px-3 pb-3" data-testid={`material-colors-${selectedFabricMaterial.id}`}>{colorStep}</div>
                                )}
                              </div>
                            </motion.div>
                          );
                          // "Best-selling" stand-in: the collection with the most colors (no sales flag exists on materials)
                          const featuredMaterial = (collection) => {
                            if (collection.materials?.length) return [...collection.materials].sort((a, b) => (b.colors?.length || 0) - (a.colors?.length || 0))[0];
                            return collection.colors?.length ? { id: collection.id, name: collection.name, colors: collection.colors } : null;
                          };
                          const renderLegacyColors = (collection) => (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden" data-testid={`collection-colors-${collection.id}`}>
                              <div className="px-3 pb-3 pt-1 border-t border-amber-200/60">
                                <div className="bg-white rounded-lg border border-neutral-200 px-3 pb-3">{colorStep}</div>
                              </div>
                            </motion.div>
                          );
                          return (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                              <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider block mb-2">{L.tier2}</Label>
                              <div className="grid grid-cols-1 gap-2" data-testid="type-accordion">
                                {selectedBrand.collections.map((collection) => {
                                  const isOpen = selectedCollection?.id === collection.id;
                                  const hasMaterials = collection.materials?.length > 0;
                                  return (
                                    <div key={collection.id} className={`border rounded-lg transition-all ${isOpen ? 'border-amber-500 bg-amber-50' : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'}`}>
                                      <CollectionHoverPreview material={featuredMaterial(collection)} label={`${L.tier2} · ${collection.name}`} title={featuredMaterial(collection)?.name} subtitle="Featured collection" disabled={isOpen}>
                                      <button
                                        onClick={() => {
                                          if (isOpen) { setSelectedCollection(null); setSelectedFabricMaterial(null); setConfig(prev => ({ ...prev, fabric_color: '', fabric_material: '', fabric_collection: undefined })); return; }
                                          setSelectedCollection(collection);
                                          setSelectedFabricMaterial(null);
                                          setConfig(prev => ({ ...prev, fabric_color: '', fabric_material: '', fabric_collection: collection.id, fabric_brand: selectedBrand?.id }));
                                        }}
                                        aria-expanded={isOpen}
                                        className="w-full p-3 text-left"
                                        data-testid={`collection-${collection.id}`}
                                      >
                                        <div className="flex justify-between items-start gap-3">
                                          <div className="min-w-0">
                                            <p className="font-medium text-neutral-900 text-sm">{collection.name}</p>
                                            {collection.fabric_type && <p className="text-xs text-neutral-500">{collection.fabric_type}</p>}
                                            {collection.openness && <p className="text-xs text-amber-600">Openness: {collection.openness}</p>}
                                          </div>
                                          <span className="flex items-center gap-2 text-xs text-neutral-400 shrink-0">
                                            {hasMaterials ? `${collection.materials.length} ${collection.materials.length === 1 ? L.tier3.toLowerCase() : L.tier3Plural.toLowerCase()}` : `${collection.colors?.length || 0} colors`}
                                            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180 text-amber-600' : ''}`} />
                                          </span>
                                        </div>
                                      </button>
                                      </CollectionHoverPreview>
                                      <AnimatePresence initial={false}>{isOpen && (hasMaterials ? renderMaterials(collection) : collection.colors?.length > 0 ? renderLegacyColors(collection) : null)}</AnimatePresence>
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Fallback to old material/color selection if no fabric collections */}
                    {fabricCollections.length === 0 && (
                      <>
                        <div className="mb-6">
                          <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Material Type</Label>
                          <div className="grid gap-2">
                            {productData?.materials?.map((material) => (
                              <button
                                key={material.id}
                                onClick={() => updateConfig('fabric_material', material.id)}
                                className={`p-3 border rounded-lg text-left transition-all ${
                                  config.fabric_material === material.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                                }`}
                                data-testid={`material-${material.id}`}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium text-neutral-900 text-sm">{material.name}</p>
                                    <p className="text-xs text-neutral-500 whitespace-pre-line">{material.description}</p>
                                  </div>
                                  <span className="font-mono text-xs text-neutral-600">from {formatPrice(material.base_price)}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        {config.fabric_material && (
                          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                            <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Color</Label>
                            <div className="flex flex-wrap gap-2">
                              {availableColors.map((color) => (
                                <TooltipProvider key={color.id}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => updateConfig('fabric_color', color.id)}
                                        className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                          config.fabric_color === color.id ? 'border-neutral-900 scale-110' : 'border-transparent hover:scale-105'
                                        }`}
                                        style={{ backgroundColor: color.hex }}
                                        data-testid={`color-${color.id}`}
                                      />
                                    </TooltipTrigger>
                                    <TooltipContent><p>{color.name}</p></TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ))}
                            </div>
                            {selectedColor && <p className="text-xs text-neutral-500 mt-2">Selected: {selectedColor.name}</p>}
                          </motion.div>
                        )}
                      </>
                    )}
                  </div>
                  {!fabricColorsInline && renderStepFooter(1)}
                </CollapsibleContent>
              </Collapsible>

              {romanSelected && (
                <FoldStyleStep
                  number={stepNumber(8)}
                  open={openSections.includes(8)}
                  onToggle={() => toggleSection(8)}
                  complete={isStepComplete(8)}
                  summary={getStepSummary(8)}
                  value={config.fold_style}
                  onChange={(f) => updateConfig('fold_style', FOLD_TO_PRICE_ID[f])}
                  prices={productData?.fold_styles || []}
                  lining={config.lining || 'unlined'}
                  onLiningChange={(l) => updateConfig('lining', l)}
                  liningPrices={productData?.linings || []}
                  onCompare={() => setCompareFoldsOpen(true)}
                  suggest={foldSuggest ? selectedColor?.name : null}
                  thumbs={foldThumbs}
                  adminThumbs={isAdmin ? { enabled: uiSettings.fold_thumbnails, onToggle: toggleFoldThumbnails } : null}
                  liningSuggest={liningSuggest ? foldLabel(priceIdToFold(config.fold_style)) : null}
                  footer={renderStepFooter(8)}
                />
              )}

              {/* Section 2: Mount Type */}
              <Collapsible open={openSections.includes(2)} onOpenChange={() => toggleSection(2)}>
                <CollapsibleTrigger className="w-full" data-testid="step-2-trigger">
                  <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    openSections.includes(2) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isStepComplete(2) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                      }`}>
                        {isStepComplete(2) ? <Check className="w-4 h-4" /> : stepNumber(2)}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-neutral-900">Mount Type</p>
                        <p className="text-sm text-neutral-500">{getStepSummary(2)}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(2) ? 'rotate-180' : ''}`} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-4 pb-2 px-2" data-testid="step-2-content">
                    <div className="grid gap-2">
                      {productData?.mount_types?.map((mount) => (
                        <button
                          key={mount.id}
                          onClick={() => updateConfig('mount_type', mount.id)}
                          className={`p-3 border rounded-lg text-left transition-all ${
                            config.mount_type === mount.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                          }`}
                          data-testid={`mount-${mount.id}`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium text-neutral-900 text-sm">{mount.name}</p>
                              <p className="text-xs text-neutral-500 whitespace-pre-line">{mount.description}</p>
                            </div>
                            {mount.price_modifier > 0 && <span className="font-mono text-xs text-neutral-600">+{formatPrice(mount.price_modifier)}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="mt-4 p-3 bg-neutral-50 rounded-lg flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-neutral-600">
                        {!config.mount_type
                          ? 'Choose how the shade will be mounted to your window.'
                          : config.mount_type === 'inside' 
                          ? 'Measure the exact width and height of your window opening.'
                          : 'Add 2-3 inches to each side for better light coverage.'}
                      </p>
                    </div>
                  </div>
                  {renderStepFooter(2)}
                </CollapsibleContent>
              </Collapsible>

              {/* Section 3: Size */}
              <Collapsible open={openSections.includes(3)} onOpenChange={() => toggleSection(3)}>
                <CollapsibleTrigger className="w-full" data-testid="step-3-trigger">
                  <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    openSections.includes(3) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isStepComplete(3) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                      }`}>
                        {isStepComplete(3) ? <Check className="w-4 h-4" /> : stepNumber(3)}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-neutral-900">Size</p>
                        <p className="text-sm text-neutral-500">{getStepSummary(3)}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(3) ? 'rotate-180' : ''}`} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-4 pb-2 px-2" data-testid="step-3-content">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Width (inches)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={config.width}
                            onChange={(e) => updateConfig('width', e.target.value === '' ? '' : parseFloat(e.target.value))}
                            placeholder="Width"
                            min={12}
                            max={144}
                            step={0.125}
                            className="text-center h-12"
                            data-testid="width-input"
                          />
                          <span className="text-neutral-400">"</span>
                        </div>
                        <p className="text-xs text-neutral-400 mt-1">12" - 144"</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Height (inches)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={config.height}
                            onChange={(e) => updateConfig('height', e.target.value === '' ? '' : parseFloat(e.target.value))}
                            placeholder="Height"
                            min={12}
                            max={120}
                            step={0.125}
                            className="text-center h-12"
                            data-testid="height-input"
                          />
                          <span className="text-neutral-400">"</span>
                        </div>
                        <p className="text-xs text-neutral-400 mt-1">12" - 120"</p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Quantity</Label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateConfig('quantity', Math.max(1, config.quantity - 1))}
                          className="w-10 h-10 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50"
                          data-testid="qty-decrease"
                        >-</button>
                        <span className="font-mono text-lg w-8 text-center">{config.quantity}</span>
                        <button
                          onClick={() => updateConfig('quantity', config.quantity + 1)}
                          className="w-10 h-10 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50"
                          data-testid="qty-increase"
                        >+</button>
                      </div>
                    </div>
                  </div>
                  {renderStepFooter(3)}
                </CollapsibleContent>
              </Collapsible>

              {/* Section 4: Control Type */}
              <Collapsible open={openSections.includes(4)} onOpenChange={() => toggleSection(4)}>
                <CollapsibleTrigger className="w-full" data-testid="step-4-trigger">
                  <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    openSections.includes(4) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isStepComplete(4) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                      }`}>
                        {isStepComplete(4) ? <Check className="w-4 h-4" /> : stepNumber(4)}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-neutral-900">Control</p>
                        <p className="text-sm text-neutral-500">{getStepSummary(4)}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(4) ? 'rotate-180' : ''}`} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-4 pb-2 px-2" data-testid="step-4-content">
                    <div className="mb-4">
                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Control Type</Label>
                      <div className="grid gap-2">
                        {productData?.control_types?.filter((c) => !isMotorPage || c.id === 'motorized').map((control) => (
                          <button
                            key={control.id}
                            onClick={() => updateConfig('control_type', control.id)}
                            className={`p-3 border rounded-lg text-left transition-all ${
                              config.control_type === control.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                            data-testid={`control-${control.id}`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-medium text-neutral-900 text-sm">{control.name}</p>
                                <p className="text-xs text-neutral-500 whitespace-pre-line">{control.description}</p>
                              </div>
                              {control.price > 0 && <span className="font-mono text-xs text-neutral-600">+{formatPrice(control.price)}</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Motorized Options - Only show when motorized is selected */}
                    {config.control_type === 'motorized' && (
                      <>
                        <div className="mb-4">
                          <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Motor Type</Label>
                          <div className="grid gap-2">
                            {[
                              { id: 'battery', name: 'Battery Powered Smart Motor', description: 'Rechargeable lithium battery, no wiring needed', price: 0 },
                              { id: 'hardwired-dc', name: 'Hardwired DC Motor', description: 'Low voltage DC power, requires transformer', price: 25 },
                              { id: 'hardwired-ac', name: 'Hardwired AC Motor', description: 'Direct AC power connection, professional install', price: 50 },
                            ].map((motor) => (
                              <button
                                key={motor.id}
                                onClick={() => updateConfig('motor_type', motor.id)}
                                className={`p-3 border rounded-lg text-left transition-all ${
                                  config.motor_type === motor.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                                }`}
                                data-testid={`motor-${motor.id}`}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium text-neutral-900 text-sm">{motor.name}</p>
                                    <p className="text-xs text-neutral-500 whitespace-pre-line">{motor.description}</p>
                                  </div>
                                  {motor.price > 0 && <span className="font-mono text-xs text-neutral-600">+{formatPrice(motor.price)}</span>}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="mb-4">
                          <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Motor Communication</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => updateConfig('motor_communication', 'two-way')}
                              className={`p-3 border rounded-lg text-center transition-all ${
                                config.motor_communication === 'two-way' ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                              }`}
                              data-testid="motor-two-way"
                            >
                              <p className="font-medium text-neutral-900 text-sm">Smart Two-Way</p>
                              <p className="text-xs text-neutral-500">Feedback & status</p>
                            </button>
                            <button
                              onClick={() => updateConfig('motor_communication', 'one-way')}
                              className={`p-3 border rounded-lg text-center transition-all ${
                                config.motor_communication === 'one-way' ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                              }`}
                              data-testid="motor-one-way"
                            >
                              <p className="font-medium text-neutral-900 text-sm">One-Way</p>
                              <p className="text-xs text-neutral-500">Basic control</p>
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {needsControlPosition && (
                    <div data-testid="control-position-group">
                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Control Position</Label>
                      <div className="flex gap-2">
                        {productData?.control_positions?.map((pos) => (
                          <button
                            key={pos.id}
                            onClick={() => updateConfig('control_position', pos.id)}
                            className={`flex-1 py-2 px-4 border rounded-lg text-sm transition-all ${
                              config.control_position === pos.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                            }`}
                            data-testid={`position-${pos.id}`}
                          >{pos.name}</button>
                        ))}
                      </div>
                    </div>
                    )}
                  </div>
                  {renderStepFooter(4)}
                </CollapsibleContent>
              </Collapsible>


              {/* Section 5: Roll Direction - Only for Roller Shades */}
              {productType === 'roller-shades' && (
              <Collapsible open={openSections.includes(5)} onOpenChange={() => toggleSection(5)}>
                <CollapsibleTrigger className="w-full" data-testid="step-5-trigger">
                  <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    openSections.includes(5) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isStepComplete(5) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                      }`}>
                        {isStepComplete(5) ? <Check className="w-4 h-4" /> : stepNumber(5)}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-neutral-900">Roll Direction</p>
                        <p className="text-sm text-neutral-500">{getStepSummary(5)}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(5) ? 'rotate-180' : ''}`} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-4 pb-2 px-2" data-testid="step-5-content">
                    <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Select Roll Direction</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => updateConfig('roll_direction', 'standard')}
                        className={`border rounded-lg overflow-hidden transition-all ${
                          config.roll_direction === 'standard' ? 'border-neutral-900 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                        data-testid="roll-standard"
                      >
                        <div className="h-24 bg-white p-2 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/fxorltzg_image.png" 
                            alt="Standard Roll" 
                            className="h-full w-auto object-contain"
                          />
                        </div>
                        <div className="p-2 bg-neutral-50">
                          <p className="font-medium text-neutral-900 text-sm">Standard Roll</p>
                          <p className="text-xs text-neutral-500">Fabric behind tube</p>
                        </div>
                      </button>
                      <button
                        onClick={() => updateConfig('roll_direction', 'reverse')}
                        className={`border rounded-lg overflow-hidden transition-all ${
                          config.roll_direction === 'reverse' ? 'border-neutral-900 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                        data-testid="roll-reverse"
                      >
                        <div className="h-24 bg-white p-2 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/o43b30ms_image.png" 
                            alt="Reverse Roll" 
                            className="h-full w-auto object-contain"
                          />
                        </div>
                        <div className="p-2 bg-neutral-50">
                          <p className="font-medium text-neutral-900 text-sm">Reverse Roll</p>
                          <p className="text-xs text-neutral-500">Fabric in front of tube</p>
                        </div>
                      </button>
                    </div>
                  </div>
                  {renderStepFooter(5)}
                </CollapsibleContent>
              </Collapsible>
              )}

              {/* Section 6: Bracket Type - Only for Roller Shades */}
              {productType === 'roller-shades' && (
              <Collapsible open={openSections.includes(6)} onOpenChange={() => toggleSection(6)}>
                <CollapsibleTrigger className="w-full" data-testid="step-6-trigger">
                  <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    openSections.includes(6) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isStepComplete(6) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                      }`}>
                        {isStepComplete(6) ? <Check className="w-4 h-4" /> : stepNumber(6)}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-neutral-900">Bracket Type</p>
                        <p className="text-sm text-neutral-500">{getStepSummary(6)}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(6) ? 'rotate-180' : ''}`} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-4 pb-2 px-2" data-testid="step-6-content">
                    <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Select Bracket Style</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => updateConfig('bracket_type', 'round')}
                        className={`border rounded-lg overflow-hidden transition-all ${
                          config.bracket_type === 'round' ? 'border-neutral-900 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                        data-testid="bracket-round"
                      >
                        <div className="h-24 bg-white p-2 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/yfj7axsz_image.png" 
                            alt="Round Bracket" 
                            className="h-full w-auto object-contain"
                          />
                        </div>
                        <div className="p-2 bg-neutral-50">
                          <p className="font-medium text-neutral-900 text-sm">Round Bracket</p>
                          <p className="text-xs text-neutral-500">Curved profile</p>
                        </div>
                      </button>
                      <button
                        onClick={() => updateConfig('bracket_type', 'square')}
                        className={`border rounded-lg overflow-hidden transition-all ${
                          config.bracket_type === 'square' ? 'border-neutral-900 ring-2 ring-neutral-900' : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                        data-testid="bracket-square"
                      >
                        <div className="h-24 bg-white p-2 flex items-center justify-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/4vx5t901_image.png" 
                            alt="Square Bracket" 
                            className="h-full w-auto object-contain"
                          />
                        </div>
                        <div className="p-2 bg-neutral-50">
                          <p className="font-medium text-neutral-900 text-sm">Square Bracket</p>
                          <p className="text-xs text-neutral-500">Angular profile</p>
                        </div>
                      </button>
                    </div>

                    {/* Bracket Color */}
                    <div className="mt-4">
                      <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3 block">Bracket Color</Label>
                      <div className="flex flex-wrap gap-2">
                        {productData?.bracket_colors?.map((bracket) => (
                          <TooltipProvider key={bracket.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => updateConfig('bracket_color', bracket.id)}
                                  className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                    config.bracket_color === bracket.id ? 'border-neutral-900 scale-110' : 'border-transparent hover:scale-105'
                                  }`}
                                  style={{ backgroundColor: bracket.hex }}
                                  data-testid={`bracket-color-${bracket.id}`}
                                />
                              </TooltipTrigger>
                              <TooltipContent><p>{bracket.name}</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ))}
                      </div>
                    </div>
                  </div>
                  {renderStepFooter(6)}
                </CollapsibleContent>
              </Collapsible>
              )}

              {/* Section 7/5: Extras - Step number varies by product */}
              <Collapsible open={openSections.includes(7)} onOpenChange={() => toggleSection(7)}>
                <CollapsibleTrigger className="w-full" data-testid="step-extras-trigger">
                  <div className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    openSections.includes(7) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isStepComplete(7) ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-500'
                      }`}>
                        {isStepComplete(7) ? <Check className="w-4 h-4" /> : stepNumber(7)}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-neutral-900">Extras</p>
                        <p className="text-sm text-neutral-500">{getStepSummary(7)}</p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${openSections.includes(7) ? 'rotate-180' : ''}`} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pt-4 pb-2 px-2" data-testid="step-extras-content">
                    <div className="space-y-2">
                      {productData?.optional_features?.filter(f => !(romanSelected && f.id === 'blackout-liner')).map((feature) => (
                        <label
                          key={feature.id}
                          className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                            config.optional_features.includes(feature.id) ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-300'
                          }`}
                          data-testid={`feature-${feature.id}`}
                        >
                          <Checkbox
                            checked={config.optional_features.includes(feature.id)}
                            onCheckedChange={() => toggleFeature(feature.id)}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <p className="font-medium text-neutral-900 text-sm">{feature.name}</p>
                              <span className="font-mono text-xs text-neutral-600">+{formatPrice(feature.price)}</span>
                            </div>
                            <p className="text-xs text-neutral-500 whitespace-pre-line">{feature.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  {renderStepFooter(7)}
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Installation Option */}
            <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="installation-option">
                <Checkbox
                  checked={config.needs_installation}
                  onCheckedChange={(checked) => updateConfig('needs_installation', checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-neutral-900">Professional Installation</p>
                      <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full font-medium">RECOMMENDED</span>
                    </div>
                    {price.installation_price > 0 && (
                      <span className="font-mono text-sm text-amber-700">+{formatPrice(price.installation_price)}</span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600 mt-1">
                    {installationPricing?.description || 'Professional installation by our certified technicians. Includes measuring verification, installation, and cleanup.'}
                  </p>
                  {installationPricing && (
                    <p className="text-xs text-neutral-500 mt-2">
                      Starting from {formatPrice(installationPricing.minimum_charge)} per window
                    </p>
                  )}
                </div>
              </label>
            </div>

            {/* Room (required) + window name */}
            <RoomLabelPicker
              room={config.room_label}
              window={config.window_label}
              onRoomChange={(v) => updateConfig('room_label', v)}
              onWindowChange={(v) => updateConfig('window_label', v)}
              highlight={isConfigReady && !roomLabelValid}
              stepNumber={stepOrder.length + 1}
            />
          </div>

          {/* Sticky Footer - stays pinned to the bottom of the configuration column */}
          <div className="sticky bottom-0 bg-white border-t border-neutral-200 px-6 py-4 z-30 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]" data-testid="configurator-footer">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider">Total</p>
                <p className="font-mono text-2xl font-medium text-neutral-900" data-testid="price-display">
                  {isConfigReady
                    ? <CountUpPrice value={price.total_price || price.total || 0} format={formatPrice} play={celebrated} />
                    : (price.total_price || price.total) > 0
                      ? <span className="text-amber-700" data-testid="price-estimate">from {formatPrice(price.total_price || price.total)}</span>
                      : '—'}
                </p>
                {!isConfigReady && (
                  <p className="text-xs text-neutral-500 mt-0.5" data-testid="complete-steps-hint">
                    {(price.total_price || price.total) > 0 ? 'Estimate — complete all steps for your final price' : 'Complete all steps to see your price'}
                  </p>
                )}
                {isConfigReady && celebrated > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-1.5 mb-0.5">
                  <motion.button type="button" onClick={shareFinished}
                    initial={{ opacity: 0, y: 6, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.9, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-amber-700"
                    data-testid="share-your-design-btn">
                    <Share2 className="w-3.5 h-3.5" /> Share your design
                  </motion.button>
                  <motion.button type="button" onClick={() => setEmailDesignOpen(true)}
                    initial={{ opacity: 0, y: 6, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 1.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-600 px-3 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50"
                    data-testid="email-your-design-btn">
                    <Mail className="w-3.5 h-3.5" /> Email it to me
                  </motion.button>
                  </div>
                )}
                {isConfigReady && !roomLabelValid && (
                  <p className="text-xs text-amber-700 mt-0.5" data-testid="room-required-footer-hint">Choose a room below to add to cart or quote</p>
                )}
                {isConfigReady && price.fabric_cost > 0 && (
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Fabric: {formatPrice(price.fabric_cost)} ({selectedFabricMaterial?.name})
                  </p>
                )}
                {isConfigReady && config.needs_installation && price.installation_price > 0 && (
                  <p className="text-xs text-amber-600 mt-0.5">
                    + {formatPrice(price.installation_price)} installation
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setShowARViewer(true)}
                  variant="outline"
                  className="h-10 px-3 text-sm"
                  data-testid="ar-preview-button"
                  disabled={!config.fabric_color || !sizeValid}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  AR Preview
                </Button>
                <Button
                  onClick={() => setShareDesignOpen(true)}
                  variant="outline"
                  className="h-10 px-3 text-sm"
                  data-testid="share-design-button"
                  disabled={!selectedColor}
                  title="Copy a link to this exact design"
                  aria-label="Share design"
                >
                  <Link2 className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => { if (!compareDesigns.length) addCurrentToCompare(); setCompareOpen(true); }}
                  variant="outline"
                  className="h-10 px-3 text-sm relative"
                  data-testid="compare-designs-button"
                  disabled={!selectedColor && compareDesigns.length === 0}
                  title="Save this design and compare up to 3 side by side"
                >
                  <Scale className="w-4 h-4 mr-2" />
                  Compare
                  {compareDesigns.length > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-600 text-white text-[10px] font-semibold px-1" data-testid="compare-count-badge">
                      {compareDesigns.length}
                    </span>
                  )}
                </Button>
                <Button
                  onClick={() => setEmailDesignOpen(true)}
                  variant="outline"
                  className="h-10 px-3 text-sm"
                  data-testid="email-design-button"
                  disabled={!selectedColor}
                  title="Email me this design"
                  aria-label="Email my design"
                >
                  <Mail className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setQuoteBuilderOpen(true)}
                  variant="outline"
                  disabled={!isOrderReady && !hasSavedQuoteItems}
                  title={isOrderReady ? 'Request a quote' : 'Complete all steps and choose a room first'}
                  className="h-10 px-3 text-sm border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="request-quote-button"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Quote
                </Button>
                <Button
                  onClick={handleSaveDesign}
                  variant="outline"
                  className="h-10 px-3 text-sm"
                  data-testid="save-design-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                {editingCartItem ? (
                  <Button
                    onClick={handleUpdateCartItem}
                    disabled={!isOrderReady || cartLoading}
                    title={isOrderReady ? 'Save changes to this cart item' : 'Complete all steps and choose a room first'}
                    className="h-10 px-4 text-sm bg-sky-700 hover:bg-sky-800 text-white"
                    data-testid="update-cart-item-button"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {cartLoading ? 'Updating...' : 'Update in Cart'}
                  </Button>
                ) : (
                <Button
                  onClick={handleAddToCart}
                  disabled={!isOrderReady || cartLoading}
                  title={isOrderReady ? 'Add to cart' : 'Complete all steps and choose a room first'}
                  className="btn-primary h-10 px-4 text-sm"
                  data-testid="add-to-cart-button"
                >
                  {isOrderReady && lastAddedKey === JSON.stringify(cartConfig()) ? <Check className="w-4 h-4 mr-2" /> : <ShoppingBag className="w-4 h-4 mr-2" />}
                  {cartLoading ? 'Adding...' : isOrderReady && lastAddedKey === JSON.stringify(cartConfig()) ? 'In Cart' : 'Add to Cart'}
                </Button>
                )}
                {(itemCount > 0 || sessionShades.length > 0) && !addedPanelOpen && (
                  <Button
                    variant="outline"
                    onClick={() => { setAddedPanelOpen(true); setTimeout(() => document.querySelector('[data-testid="add-another-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120); }}
                    className="h-10 px-3 text-sm"
                    title="Add another shade to this order"
                    data-testid="add-another-shade-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Another shade
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setSaveConfigDialogOpen(true)}
                  disabled={!isOrderReady}
                  className="h-10"
                  title="Save as Preset"
                  data-testid="save-preset-button"
                >
                  <Bookmark className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* Recently Viewed Products */}
      <RecentlyViewed currentProductId={productType} maxItems={6} />

      {/* Sample Request Modal */}
      {/* Sample Request Modal */}
      <SampleRequestModal
        open={sampleModalOpen}
        onOpenChange={setSampleModalOpen}
        selectedSampleColors={selectedSampleColors}
        setSelectedSampleColors={setSelectedSampleColors}
        sampleRequest={sampleRequest}
        setSampleRequest={setSampleRequest}
        onSubmit={handleSampleRequestSubmit}
        submitting={submittingSample}
      />

      {/* Material Comparison Modal - Extracted Component */}
      <MaterialCompareModal
        open={compareModalOpen}
        onOpenChange={setCompareModalOpen}
        materialsToCompare={materialsToCompare}
        setMaterialsToCompare={setMaterialsToCompare}
        toggleMaterialComparison={toggleMaterialComparison}
        onRequestSamples={() => {
          setCompareModalOpen(false);
          setSampleModalOpen(true);
        }}
      />

      <FabricGalleryDialog
        open={fabricGalleryOpen}
        onOpenChange={setFabricGalleryOpen}
        brands={fabricCollections}
        selectedColorId={config.fabric_color}
        onSelect={(entry) => {
          applyFabricSelection(entry);
          toast.success(`${entry.name} selected`);
        }}
      />

      {/* Color Zoom Modal - Extracted Component */}
      <ColorZoomModal
        open={colorZoomOpen}
        onOpenChange={setColorZoomOpen}
        zoomedColor={zoomedColor}
        config={config}
        updateConfig={updateConfig}
        selectedFabricMaterial={selectedFabricMaterial}
        selectedCollection={selectedCollection}
        selectedBrand={selectedBrand}
        isAdmin={isAdmin}
        editingColor={editingColor}
        setEditingColor={setEditingColor}
        editColorData={editColorData}
        setEditColorData={setEditColorData}
        savingColor={savingColor}
        saveColorChanges={saveColorChanges}
        isColorFavorite={isColorFavorite}
        toggleColorFavorite={toggleColorFavorite}
        loadingFavorites={loadingFavorites}
        isColorSelectedForSample={isColorSelectedForSample}
        toggleSampleColor={toggleSampleColor}
      />

      {/* Save Config as Preset Dialog */}
      <SaveConfigDialog
        open={saveConfigDialogOpen}
        onOpenChange={setSaveConfigDialogOpen}
        configName={configName}
        setConfigName={setConfigName}
        onSave={handleSaveConfig}
        saving={savingConfig}
        config={config}
        selectedFabricMaterial={selectedFabricMaterial}
        selectedCollection={selectedCollection}
        selectedColor={selectedColor}
      />

      {/* Quote Builder Dialog */}
      <QuoteBuilder
        open={quoteBuilderOpen}
        onOpenChange={setQuoteBuilderOpen}
        currentVisualization={currentVisualization}
        selectedColor={selectedColor}
        selectedBrand={selectedBrand}
        selectedCollection={selectedCollection}
        selectedMaterial={selectedFabricMaterial}
        config={{ ...config, room_label: fullRoomLabel }}
        productType={productType}
        canAdd={isOrderReady}
        onItemsChange={setHasSavedQuoteItems}
      />
    </Layout>
  );
};
